import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import ServiceToggle from './ServiceToggle';
import { X, Zap, DollarSign, MapPin, FileText, Plus as PlusIcon, Settings, Layers, Calendar as CalendarIcon, CalendarX, Check, Image, ChevronDown, Trash2, Lightbulb, HelpCircle, Globe, Info, AlertTriangle, Phone, Plus, Clock, CalendarDays, Percent, Ticket, Star } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import apiV2 from '../utils/apiV2';
import CustomDropdown from './ui/custom-dropdown';
import { TIMEZONES, DEFAULT_TIMEZONE } from '../utils/timezones';
import { CURRENCIES, TAX_TYPES, DEFAULT_CURRENCY, DEFAULT_TAX_TYPE, DEFAULT_TAX_RATE, getDefaultTaxType, getDefaultTaxRate } from '../utils/currencyTax';
import { getCountryConfig, getCurrencyOptions } from '../config/countries';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import WizardStepHeader from './WizardStepHeader';
import MoneyInput from './MoneyInput';
import { PhoneInput } from './PhoneInput';
import { sanitizeText } from '../utils/sanitize';

const CreateServiceWizard = ({ onComplete, onCancel, editingService = null, assets = [] }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [showAIDescriptionModal, setShowAIDescriptionModal] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [showStripeWarning, setShowStripeWarning] = useState(false);
  const [orgCountry, setOrgCountry] = useState('USA');
  const locale = useOperatorLocale();
  
  // Get org ID for v2 API calls
  const orgId = localStorage.getItem('organizationId');
  
  // Backend URL for API calls
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  
  // AI Cooldown timers
  const [aiCooldowns, setAiCooldowns] = useState({
    polishName: 0,
    tagline: 0,
    description: 0,
    inclusions: 0,
    sections: 0,
    addons: 0
  });
  
  const startCooldown = (feature) => {
    setAiCooldowns(prev => ({ ...prev, [feature]: 7 }));
    const interval = setInterval(() => {
      setAiCooldowns(prev => {
        const newValue = prev[feature] - 1;
        if (newValue <= 0) {
          clearInterval(interval);
          return { ...prev, [feature]: 0 };
        }
        return { ...prev, [feature]: newValue };
      });
    }, 1000);
  };
  
  const getInitialFormData = () => {
    if (editingService) {
      return {
        // Basic Info
        name: editingService.name || '', 
        tagline: editingService.tagline || '',
        description: editingService.description || '', 
        images: editingService.images || [],
        mainImageIndex: editingService.mainImageIndex || 0,
        
        // Pricing
        pricingModel: editingService.pricingModel || 'fixed',
        basePrice: editingService.price || editingService.basePrice || 150,
        currency: editingService.currency || 'USD',
        durationOptions: editingService.durationOptions || [{ duration: 60, price: 150, enabled: true }],
        offPeakPricing: editingService.offPeakPricing || { enabled: false, days: [], prices: {} },
        priceDisplayMode: editingService.priceDisplayMode || 'hourly',
        dynamicPricingEnabled: editingService.dynamicPricingEnabled || false,
        pricingMultipliers: editingService.pricingMultipliers || {
          monday: 0, tuesday: 0, wednesday: 0, thursday: 0,
          friday: 0, saturday: 0, sunday: 0
        },
        // Specific date price overrides (e.g., 4th of July)
        dateOverrides: editingService.dateOverrides || [],
        // Seasonal pricing (date range based rate tiers)
        seasonalPricing: editingService.seasonalPricing || [],
        
        // Deposit settings
        depositEnabled: (editingService.depositAmount && editingService.depositAmount > 0) || false,
        depositAmount: editingService.depositAmount || 25,
        depositType: editingService.depositType || 'percentage',
        
        // Duration
        isMultiDay: editingService.isMultiDay || false,
        durationDays: editingService.durationDays || 1,
        
        // Location
        country: editingService.country || 'USA',
        city: editingService.city || '', 
        state: editingService.state || '',
        locationDetails: editingService.locationDetails || '',
        
        // Tax
        taxEnabled: editingService.taxEnabled || editingService.salesTaxEnabled || false,
        taxType: editingService.taxType || 'tax',
        taxRate: editingService.taxRate || editingService.salesTaxRate || 0,
        salesTaxEnabled: editingService.salesTaxEnabled || editingService.taxEnabled || false,
        salesTaxRate: editingService.salesTaxRate || editingService.taxRate || 0,
        // Tax inclusivity is an explicit operator choice (no auto-default)
        taxInclusive: typeof editingService.taxInclusive === 'boolean' ? editingService.taxInclusive : null,
        
        // 📜 Service Fee - Owner can add extra fee to offset processing costs
        serviceFeeEnabled: editingService.serviceFeeEnabled || editingService.service_fee_enabled || false,
        serviceFeeType: editingService.serviceFeeType || editingService.service_fee_type || 'percentage',
        serviceFeeValue: editingService.serviceFeeValue || editingService.service_fee_value || 0,
        serviceFeeLabel: editingService.serviceFeeLabel || editingService.service_fee_label || 'Service Fee',
        includeInRecommendations: editingService.includeInRecommendations !== false,
        
        // Fee structure: who pays platform + processing fees
        feeStructure: editingService.feeStructure || editingService.fee_structure || 'customer_pays',
        
        // Content Sections
        whatsIncluded: editingService.whatsIncluded || [],
        customSections: editingService.customSections || [],
        customCancellationPolicy: editingService.customCancellationPolicy || '',
        disclaimers: editingService.disclaimers || '',
        requireWaiver: editingService.requireWaiver !== false, // Default true
        
        // Add-ons & Questions
        addOns: editingService.addons || editingService.addOns || [],
        customQuestions: editingService.customQuestions || [],
        
        // Capacity & Booking
        maxGroupSize: editingService.maxCapacity || editingService.capacity || editingService.maxGroupSize || 12,
        bookingType: editingService.bookingMode || editingService.capacityMode || 'per_ticket',
        capacityModeOld: editingService.capacityMode,
        
        // Assets
        assetIds: editingService.assetIds || editingService.linkedAssets || [],
        letCustomersChooseAsset: editingService.letCustomersChooseAsset || editingService.allowCustomerAssetSelection || false,
        
        // Scheduling
        timezone: editingService.timezone || 'America/Chicago',
        minimumNotice: editingService.minimumNoticeHours || 2,
        availabilityRules: editingService.availabilityRules || {},
        
        // Time Slots
        availableTimeSlots: (() => {
          const slots = editingService.availableTimeSlots || editingService.timeSlots || [];
          if (slots.length === 0) return [];
          // Check if old format (array of strings)
          if (typeof slots[0] === 'string') {
            return slots.map(time => ({
              time,
              days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
            }));
          }
          return slots;
        })(),
        
        // Contact & Redirects
        emergencyContactNumber: editingService.emergencyContactNumber || '',
        successRedirectUrl: editingService.successRedirectUrl || '',
        cancelRedirectUrl: editingService.cancelRedirectUrl || '',
        
        // Status
        active: editingService.active !== false && editingService.isActive !== false,
        
        // Availability date range
        availabilityStartDate: editingService.availabilityStartDate || null,
        availabilityEndDate: editingService.availabilityEndDate || null,
        
        // Unavailability slots
        unavailabilitySlots: editingService.unavailabilitySlots || [],

        // ── Duration model & flexible dates ──
        durationModel: editingService.durationModel || 'slot_based',
        checkInTime: editingService.checkInTime || '15:00',
        checkOutTime: editingService.checkOutTime || '11:00',
        minStayNights: editingService.minStayNights || null,
        maxStayNights: editingService.maxStayNights || null,

        // ── Day Pass (whole-day access) ──
        dayPassCapacity: editingService.maxCapacity || editingService.capacity || 50,
        dayPassDays: (() => {
          // Reverse-map operating days from the synthetic all-day slot
          const slots = editingService.availableTimeSlots || editingService.timeSlots || [];
          if (editingService.durationModel === 'day_pass' && slots.length > 0 && slots[0]?.days?.length) {
            return slots[0].days;
          }
          return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        })(),

        // ── Dynamic pricing layers ──
        inventoryPricing: editingService.inventoryPricing || [],
        timePricing: editingService.timePricing || [],
      };
    }
    
    const savedDraft = localStorage.getItem('serviceWizardDraft');
    if (savedDraft) {
      try { return JSON.parse(savedDraft); } catch { /* Invalid JSON, ignore */ }
    }
    
    return {
      name: '', tagline: '', description: '', images: [], mainImageIndex: 0,
      pricingModel: 'fixed', basePrice: 150,
      durationOptions: [{ duration: 60, price: 150, enabled: true }],
      city: '', state: '', salesTaxEnabled: false, salesTaxRate: 0,
      whatsIncluded: [], customSections: [], addOns: [],
      customQuestions: [], // NEW: Custom questions for widget
      customCancellationPolicy: '', disclaimers: '',
      maxGroupSize: 12, bookingType: 'per_ticket', assetIds: [],
      letCustomersChooseAsset: false, 
      timezone: DEFAULT_TIMEZONE,
      currency: 'USD',
      country: 'USA',
      locationDetails: '',
      taxEnabled: false,
      taxType: 'tax', // Universal tax label
      taxRate: 0,
      taxInclusive: null, // operator explicitly chooses inclusive vs exclusive
      // 📜 Service Fee defaults
      serviceFeeEnabled: false,
      serviceFeeType: 'percentage',
      serviceFeeValue: 0,
      serviceFeeLabel: 'Service Fee',
      // Fee structure: who pays
      feeStructure: 'customer_pays',
      minimumNotice: 2, active: true,
      availableTimeSlots: [],
      availabilityRules: {},
      isMultiDay: false,
      durationDays: 1,
      dynamicPricingEnabled: false,
      offPeakPricing: { enabled: false, days: [], prices: {} },
      priceDisplayMode: 'hourly',
      pricingMultipliers: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0
      },
      dateOverrides: [], // Specific date price overrides (e.g., {date: '2026-07-04', multiplier: 25, name: '4th of July'})
      seasonalPricing: [], // Seasonal rate tiers
      depositEnabled: false,
      depositAmount: 25,
      depositType: 'percentage',
      // ── Duration model ──
      durationModel: 'slot_based',
      checkInTime: '15:00',
      checkOutTime: '11:00',
      minStayNights: null,
      maxStayNights: null,
      // ── Day Pass (whole-day access) ──
      dayPassCapacity: 50,
      dayPassDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
      // ── Dynamic pricing layers ──
      inventoryPricing: [],
      timePricing: [],
    };
  };
  
  const [formData, setFormData] = useState(getInitialFormData());
  const totalSteps = 15;
  const stepTitles = ['Basics', 'Images', 'Pricing', formData.durationModel === 'flexible_dates' ? 'Check-in/Out' : formData.durationModel === 'day_pass' ? 'Operating Days' : 'Start Times', 'Unavailability', 'Dynamic Pricing', 'Location', 'Safety', 'Details', 'Add-ons', 'Questions', 'Policies', 'Assets', 'Settings', 'Review'];

  // Check org details on mount
  React.useEffect(() => {
    const loadOrgDetails = async () => {
      try {
        const response = await apiV2.get('/auth/organizations/me');
        const org = response.data;
        
        // Get org country and update defaults
        const country = org.country || 'USA';
        setOrgCountry(country);
        
        // Only update if creating new service (not editing)
        if (!editingService) {
          const countryConfig = getCountryConfig(country);
          // Use org's timezone if set, otherwise fall back to country default
          const orgTimezone = org.timezone || countryConfig.defaultTimezone;
          setFormData(prev => ({
            ...prev,
            country: country,
            currency: countryConfig.currency.code,
            timezone: orgTimezone,
            taxEnabled: countryConfig.tax.enabled,
            taxType: 'tax', // Universal tax label
            taxRate: countryConfig.tax.defaultRate
          }));
        }
      } catch (error) {
        console.error('Error loading org details:', error);
      }
    };
    loadOrgDetails();
  }, [editingService]);

  const updateFormData = (field, value) => {
    setFormData(prevData => {
      const newData = { ...prevData, [field]: value };
      if (!editingService) {
        localStorage.setItem('serviceWizardDraft', JSON.stringify(newData));
      }
      return newData;
    });
  };

  const nextStep = () => {
    if (validateCurrentStep()) setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.name.trim()) { toast.error('Service name required'); return false; }
        if (!formData.description.trim()) { toast.error('Description required'); return false; }
        return true;
      case 2:
        // Images are optional - can be added later
        return true;
      case 3:
        if (formData.durationModel === 'flexible_dates') {
          // Date range just needs a base price
          if (!formData.durationOptions?.[0]?.price || formData.durationOptions[0].price <= 0) {
            toast.error('Base nightly rate must be greater than 0'); return false;
          }
          return true;
        }
        if (formData.durationModel === 'day_pass') {
          // Day pass needs a per-pass price; capacity comes from linked assets
          if (!formData.durationOptions?.[0]?.price || formData.durationOptions[0].price <= 0) {
            toast.error('Pass price must be greater than 0'); return false;
          }
          return true;
        }
        if (!formData.durationOptions || formData.durationOptions.length === 0) {
          toast.error('Add at least one price and duration'); return false;
        }
        if (formData.durationOptions.some(opt => !opt.price || opt.price <= 0)) {
          toast.error('Price must be greater than 0'); return false;
        }
        if (formData.durationOptions.some(opt => !opt.duration || opt.duration <= 0)) {
          toast.error('Duration must be greater than 0'); return false;
        }
        return true;
      case 4:
        // Date range doesn't use time slots
        if (formData.durationModel === 'flexible_dates') return true;
        // Day pass: require at least one operating day
        if (formData.durationModel === 'day_pass') {
          if (!formData.dayPassDays || formData.dayPassDays.length === 0) {
            toast.error('Select at least one operating day'); return false;
          }
          return true;
        }
        const slots = formData.availableTimeSlots || [];
        if (slots.length === 0) { 
          toast.error('Add at least one time slot'); 
          return false; 
        }
        const hasActiveSlot = slots.some(slot => slot.days && slot.days.length > 0);
        if (!hasActiveSlot) {
          toast.error('At least one time slot must have days selected');
          return false;
        }
        return true;
      case 5:
        // Unavailability - optional, no validation needed
        return true;
      case 6:
        // Dynamic pricing - optional, no validation needed
        return true;
      case 7:
        if (!formData.country) { toast.error('Country is required'); return false; }
        // State is required for US
        if (formData.country === 'US' && (!formData.state || !formData.state.trim())) {
          toast.error('State is required for US locations');
          return false;
        }
        if (!formData.city.trim()) { toast.error('City is required'); return false; }
        if (!formData.locationDetails || !formData.locationDetails.trim()) { 
          toast.error('Meeting Point is required - customers need to know where to go'); 
          return false; 
        }
        if (formData.taxEnabled && (formData.taxInclusive === null || formData.taxInclusive === undefined)) {
          toast.error('Choose whether tax is inclusive or exclusive');
          return false;
        }
        return true;
      case 8:
        // Safety - emergency contact is optional
        return true;
      case 9:
        if (formData.whatsIncluded.length === 0) {
          toast.error('Add at least one item to What\'s Included'); return false;
        }
        return true;
      case 10:
        // Validate add-ons have proper values ($0 is allowed — e.g. agreement checkboxes)
        if (formData.addOns && formData.addOns.length > 0) {
          for (let addon of formData.addOns) {
            if (addon.type === 'checkbox' && (isNaN(Number(addon.price)) || Number(addon.price) < 0)) {
              toast.error(`Add-on "${addon.title}" can't have a negative price`);
              return false;
            }
            if (addon.type === 'percentage' && (!addon.percentage || addon.percentage <= 0)) {
              toast.error(`Add-on "${addon.title}" needs a percentage value`);
              return false;
            }
          }
        }
        return true;
      case 11:
        // Custom Questions - optional, no validation needed
        return true;
      case 12:
        // Policies - cancellation and disclaimers required
        if (!formData.customCancellationPolicy.trim()) { toast.error('Cancellation policy is required'); return false; }
        if (!formData.disclaimers.trim()) { toast.error('Disclaimers are required'); return false; }
        return true;
      case 13:
        // Assets - at least one required
        if (formData.assetIds.length === 0) { toast.error('Link at least one asset'); return false; }
        return true;
      case 14:
        // Settings - timezone is required
        if (!formData.timezone || !formData.timezone.trim()) { 
          toast.error('Timezone is required'); 
          return false; 
        }
        return true;
      default: return true;
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (formData.images.length + files.length > 10) { toast.error('Max 10 images'); return; }
    setIsUploadingImages(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const fd = new FormData(); fd.append('file', file);
        const res = await api.post('/upload/service-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        return res.data.url;
      });
      const urls = await Promise.all(uploadPromises);
      updateFormData('images', [...formData.images, ...urls]);
      toast.success(`${files.length} image(s) uploaded`);
    } catch (error) { 
      toast.error('Upload failed'); 
    } finally {
      setIsUploadingImages(false);
    }
  };

  const removeImage = (i) => {
    const newImages = formData.images.filter((_, idx) => idx !== i);
    updateFormData('images', newImages);
    if (formData.mainImageIndex >= newImages.length) {
      updateFormData('mainImageIndex', Math.max(0, newImages.length - 1));
    }
  };

  const setMainImage = (i) => updateFormData('mainImageIndex', i);



  const handleGenerateDescription = async (userInput) => {
    startCooldown('description'); // START IMMEDIATELY
    setGeneratingAI(true);
    try {
      const response = await fetch(`${backendUrl}/api/ai/generate-description`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          input: userInput,
          serviceName: formData.name || 'Service'
        })
      });
      
      if (!response.ok) throw new Error('Failed to generate');
      
      const data = await response.json();
      const fullText = sanitizeText(data.description, { maxLength: 2000 });
      
      setShowAIDescriptionModal(false);
      
      // Typewriter effect
      let currentText = '';
      for (let i = 0; i < fullText.length; i++) {
        currentText += fullText[i];
        updateFormData('description', currentText);
        await new Promise(resolve => setTimeout(resolve, 20)); // 20ms per character (faster for longer text)
      }
      
      toast.success('✨ Description generated!');
    } catch (error) {
      toast.error('Failed to generate description');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;
    setIsSubmitting(true);
    try {
      // Auto-detect pricing model based on duration options
      const pricingModel = formData.durationOptions.length > 1 ? 'duration_based' : 'fixed';

      // ── Day Pass: build a synthetic all-day slot so existing capacity/availability/booking infra works ──
      const isDayPass = formData.durationModel === 'day_pass';
      const dayPassPrice = formData.durationOptions[0]?.price || 0;
      // Capacity is ALWAYS auto-derived from the currently-linked assets (numeric sum).
      // We never trust the stored/loaded maxGroupSize directly because a stale or stringified
      // value previously caused string-concatenation bugs (e.g. "12" + "24" => "1224"), which
      // the backend then rejected ("capacity exceeds total asset capacity").
      const linkedAssetIds = formData.assetIds || [];
      const linkedAssetsCapacity = (assets || [])
        .filter(a => linkedAssetIds.includes(a.id))
        .reduce((sum, a) => sum + (parseInt(a.capacity, 10) || 0), 0);
      const fallbackCapacity = parseInt(formData.maxGroupSize, 10) || 12;
      const effectiveCapacity = linkedAssetIds.length > 0
        ? (linkedAssetsCapacity || fallbackCapacity)
        : fallbackCapacity;

      const dayPassSlots = [{ time: '00:00', days: formData.dayPassDays || ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'], label: 'All day' }];
      const effectiveDurationOptions = isDayPass
        ? [{ duration: 1440, price: dayPassPrice, enabled: true }]
        : (formData.durationOptions || []);
      const effectiveTimeSlots = isDayPass ? dayPassSlots : (formData.availableTimeSlots || []);

      // Build complete service payload with ALL fields
      const serviceData = {
        // Basic Info
        name: formData.name,
        tagline: formData.tagline || '',
        description: formData.description || '',
        
        // Pricing
        pricingModel,
        price: formData.durationOptions[0]?.price || 0,
        basePrice: formData.durationOptions[0]?.price || 0,
        currency: formData.currency || 'USD',
        durationOptions: effectiveDurationOptions,
        dynamicPricingEnabled: formData.dynamicPricingEnabled || false,
        pricingMultipliers: formData.pricingMultipliers || {},
        offPeakPricing: formData.offPeakPricing || null,
        priceDisplayMode: formData.priceDisplayMode || 'hourly',
        dateOverrides: formData.dateOverrides || [], // Specific date overrides
        seasonalPricing: formData.seasonalPricing || [], // Seasonal rate tiers
        
        // Deposit
        depositAmount: formData.depositEnabled ? (formData.depositAmount || 0) : 0,
        depositType: formData.depositType || 'percentage',
        
        // Duration
        duration: effectiveDurationOptions[0]?.duration || 60,
        isMultiDay: formData.isMultiDay || false,
        durationDays: formData.durationDays || 1,

        // ── Duration model & flexible dates ──
        durationModel: formData.durationModel || 'slot_based',
        checkInTime: formData.checkInTime || '15:00',
        checkOutTime: formData.checkOutTime || '11:00',
        minStayNights: formData.minStayNights || null,
        maxStayNights: formData.maxStayNights || null,

        // ── Dynamic pricing layers ──
        inventoryPricing: formData.inventoryPricing || [],
        timePricing: formData.timePricing || [],
        
        // Capacity
        capacity: effectiveCapacity,
        maxGroupSize: effectiveCapacity,
        minGroupSize: 1,
        bookingMode: isDayPass ? 'per_ticket' : formData.bookingType,
        capacityMode: isDayPass ? 'per_ticket' : (formData.bookingType === 'exclusive' ? 'per_asset' : 'per_ticket'),
        capacitySource: isDayPass ? 'assets' : (formData.capacitySource || 'service'),
        
        // Assets
        linkedAssets: formData.assetIds || [],
        assetIds: formData.assetIds || [],
        allowCustomerAssetSelection: formData.letCustomersChooseAsset || false,
        letCustomersChooseAsset: formData.letCustomersChooseAsset || false,
        
        // Scheduling
        timezone: formData.timezone || 'America/Chicago',
        minimumNoticeHours: formData.minimumNotice || 2,
        minimumNotice: formData.minimumNotice || 2,
        advanceBookingDays: 365,
        
        // Time Slots - CRITICAL for widget
        availableTimeSlots: effectiveTimeSlots,
        timeSlots: effectiveTimeSlots,

        // Advanced schedule rules (daily end cap + shift boundary)
        availabilityRules: formData.availabilityRules || {},
        
        // Location
        country: formData.country || 'USA',
        city: formData.city || '',
        state: formData.state || '',
        locationDetails: formData.locationDetails || '',
        
        // Tax - send both naming conventions
        taxEnabled: formData.taxEnabled || false,
        taxType: formData.taxType || 'tax',
        taxRate: formData.taxRate || 0,
        salesTaxEnabled: formData.taxEnabled || false,
        salesTaxRate: formData.taxRate || 0,
        taxInclusive: formData.taxInclusive ?? false,
        includeInRecommendations: formData.includeInRecommendations !== false,
        
        // 📜 Service Fee - Owner-configurable fee to offset processing costs
        serviceFeeEnabled: formData.serviceFeeEnabled || false,
        service_fee_enabled: formData.serviceFeeEnabled || false,
        serviceFeeType: formData.serviceFeeType || 'percentage',
        service_fee_type: formData.serviceFeeType || 'percentage',
        serviceFeeValue: parseFloat(formData.serviceFeeValue) || 0,
        service_fee_value: parseFloat(formData.serviceFeeValue) || 0,
        serviceFeeLabel: formData.serviceFeeLabel || 'Service Fee',
        service_fee_label: formData.serviceFeeLabel || 'Service Fee',
        
        // Fee structure: who pays the platform + processing fees
        feeStructure: formData.feeStructure || 'customer_pays',
        fee_structure: formData.feeStructure || 'customer_pays',
        
        // Content Sections
        whatsIncluded: formData.whatsIncluded || [],
        customSections: formData.customSections || [],
        customCancellationPolicy: formData.customCancellationPolicy || '',
        disclaimers: formData.disclaimers || '',
        requireWaiver: formData.requireWaiver !== false,
        
        // Add-ons & Questions
        addons: formData.addOns || [],
        customQuestions: formData.customQuestions || [],
        
        // Media
        images: formData.images || [],
        mainImageIndex: formData.mainImageIndex || 0,
        
        // Contact & Redirects
        emergencyContactNumber: formData.emergencyContactNumber || '',
        successRedirectUrl: formData.successRedirectUrl || '',
        cancelRedirectUrl: formData.cancelRedirectUrl || '',
        
        // Availability date range
        availabilityStartDate: formData.availabilityStartDate || null,
        availabilityEndDate: formData.availabilityEndDate || null,
        
        // Unavailability slots
        unavailabilitySlots: formData.unavailabilitySlots || [],
        
        // Status
        isActive: formData.active !== false,
      };
      
      // Debug logging (uncomment to troubleshoot)

      const response = editingService?.id
        ? await apiV2.put(`/organizations/${orgId}/services/${editingService.id}`, serviceData)
        : await apiV2.post(`/organizations/${orgId}/services`, serviceData);

      toast.success(editingService?.id ? 'Service updated!' : 'Service created!');
      
      // If the backend capped capacity to the linked assets' physical limit, tell the operator
      const adj = response.data?.capacityAdjusted;
      if (adj) {
        toast.info(`Max capacity set to ${adj.adjustedTo} to match your linked assets (you entered ${adj.requested}).`, { duration: 6000 });
      }
      
      // Notify setup guide of data change
      window.dispatchEvent(new Event('setupDataChanged'));
      
      localStorage.removeItem('serviceWizardDraft');
      onComplete(response.data);
    } catch (error) {
      let errorMessage = 'Failed to save';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (Array.isArray(errorData.detail)) errorMessage = errorData.detail[0].msg || JSON.stringify(errorData.detail[0]);
        else if (typeof errorData.detail === 'string') errorMessage = errorData.detail;
        else if (typeof errorData.detail === 'object') errorMessage = errorData.detail.msg || 'Validation error';
      }
      toast.error(errorMessage);
    } finally { setIsSubmitting(false); }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return <Step1Basics formData={formData} updateFormData={updateFormData} setShowAIDescriptionModal={setShowAIDescriptionModal} aiCooldowns={aiCooldowns} startCooldown={startCooldown} backendUrl={backendUrl} />;
      case 2: return <Step2Images formData={formData} updateFormData={updateFormData} handleImageUpload={handleImageUpload} removeImage={removeImage} setMainImage={setMainImage} isUploadingImages={isUploadingImages} />;
      case 3: return <Step3Pricing formData={formData} updateFormData={updateFormData} orgCountry={orgCountry} />;
      case 4: return <Step4Availability formData={formData} updateFormData={updateFormData} />;
      case 5: return <Step5Unavailability formData={formData} updateFormData={updateFormData} />;
      case 6: return <Step6DynamicPricing formData={formData} updateFormData={updateFormData} />;
      case 7: return <Step7Location formData={formData} updateFormData={updateFormData} orgCountry={orgCountry} />;
      case 8: return <Step8Safety formData={formData} updateFormData={updateFormData} />;
      case 9: return <Step9Details formData={formData} updateFormData={updateFormData} aiCooldowns={aiCooldowns} startCooldown={startCooldown} backendUrl={backendUrl} />;
      case 10: return <Step10AddOns formData={formData} updateFormData={updateFormData} aiCooldowns={aiCooldowns} startCooldown={startCooldown} backendUrl={backendUrl} />;
      case 11: return <Step11CustomQuestions formData={formData} updateFormData={updateFormData} />;
      case 12: return <Step12Policies formData={formData} updateFormData={updateFormData} />;
      case 13: return <Step13Assets formData={formData} updateFormData={updateFormData} assets={assets} />;
      case 14: return <Step14Settings formData={formData} updateFormData={updateFormData} orgCountry={orgCountry} />;
      case 15: return <Step15Review formData={formData} orgCountry={orgCountry} />;
      default: return null;
    }
  };

  return (
    <>
      <StripeConnectWarning show={showStripeWarning} onClose={() => setShowStripeWarning(false)} />
      
      <div className="flex flex-col h-full min-h-0">
        {/* Header */}
        <div className="pb-3 mb-3 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {editingService?.id ? 'Edit Service' : editingService ? 'Duplicate Service' : 'New Service'}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Step {currentStep} of {totalSteps} — {stepTitles[currentStep - 1]}
              </p>
            </div>
          </div>
          <div className="relative h-1.5 bg-gray-100 rounded-full">
            <div 
              className="absolute h-full bg-blue-600 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto min-h-0 px-0.5">
          {renderStepContent()}
        </div>

        {/* Footer nav — sticky to bottom on mobile */}
        <div className="border-t border-gray-100 pt-3 mt-3 flex items-center justify-between gap-3 flex-shrink-0 bg-white sm:bg-transparent sticky bottom-0 pb-safe pb-2 sm:pb-0 sm:static">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex-1 sm:flex-none outline-none focus:outline-none"
            data-testid="wizard-back-btn"
          >
            Back
          </Button>
          <span className="text-xs text-gray-400 hidden sm:block">
            {currentStep} / {totalSteps}
          </span>
          {currentStep === totalSteps ? (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none outline-none focus:outline-none"
              data-testid="wizard-submit-btn"
            >
              {isSubmitting ? 'Saving...' : editingService?.id ? 'Update' : 'Create'}
            </Button>
          ) : (
            <Button
              onClick={nextStep}
              className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none outline-none focus:outline-none"
              data-testid="wizard-next-btn"
            >
              Next
            </Button>
          )}
        </div>

        <AIDescriptionModal
          isOpen={showAIDescriptionModal}
          onClose={() => setShowAIDescriptionModal(false)}
          onGenerate={handleGenerateDescription}
          isGenerating={generatingAI}
        />
      </div>
    </>
  );
};

const Step1Basics = ({ formData, updateFormData, setShowAIDescriptionModal, aiCooldowns, startCooldown, backendUrl }) => (
  <div className="space-y-4 sm:space-y-6">
    <WizardStepHeader icon={Zap} tone="blue" title="Service basics" subtitle="Name, tagline, and description" />
    <div>
      <Label>Service Name <span className="text-red-500">*</span></Label>
      <Input value={formData.name} onChange={(e) => updateFormData('name', e.target.value)} placeholder="2 Hour Sunset Cruise" className="mt-1" data-testid="service-name-input" />
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-gray-500">Main service title</p>
        {formData.name && formData.name.trim().length > 3 ? (
          <button
            type="button"
            disabled={aiCooldowns.polishName > 0}
            onClick={async () => {
              if (aiCooldowns.polishName > 0) return;
              startCooldown('polishName'); // START IMMEDIATELY
              
              try {
                toast.info('✨ AI is polishing...');
                const response = await fetch(`${backendUrl}/api/ai/polish-name`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ serviceName: formData.name })
                });
                
                if (!response.ok) {
                  const errorText = await response.text();
                  console.error('Polish name error:', response.status, errorText);
                  throw new Error(`Failed to polish: ${response.status}`);
                }
                
                const data = await response.json();
                const fullText = sanitizeText(data.polishedName, { singleLine: true, maxLength: 80 });
                
                // Typewriter effect
                updateFormData('name', '');
                await new Promise(resolve => setTimeout(resolve, 100));
                let currentText = '';
                for (let i = 0; i < fullText.length; i++) {
                  currentText += fullText[i];
                  updateFormData('name', currentText);
                  await new Promise(resolve => setTimeout(resolve, 30));
                }
                
                toast.success('✨ Name polished!');
              } catch (error) {
                console.error('Polish name failed:', error);
                toast.error('Failed to polish name');
              }
            }}
            className={`text-xs font-medium whitespace-nowrap flex items-center gap-1 ${
              aiCooldowns.polishName > 0 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-blue-600 hover:text-blue-700'
            }`}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            {aiCooldowns.polishName > 0 ? aiCooldowns.polishName : 'Polish with AI'}
          </button>
        ) : (
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            Polish with AI
          </span>
        )}
      </div>
    </div>
    <div>
      <Label>Tagline</Label>
      <Input 
        value={formData.tagline} 
        onChange={(e) => updateFormData('tagline', e.target.value)} 
        placeholder="Captain, Ice, Cooler, Floating Mat" 
        maxLength={60}
        className="mt-1"
        data-testid="service-tagline-input"
      />
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-gray-500">Quick summary (max 60 chars) • Appears on service cards</p>
        {formData.name ? (
          <button
            type="button"
            disabled={aiCooldowns.tagline > 0}
            onClick={async () => {
              if (aiCooldowns.tagline > 0) return;
              startCooldown('tagline'); // START IMMEDIATELY
              
              try {
                toast.info('✨ AI is writing...');
                const response = await fetch(`${backendUrl}/api/ai/generate-tagline`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ serviceName: formData.name })
                });
                
                if (!response.ok) throw new Error('Failed to generate');
                
                const data = await response.json();
                const fullText = sanitizeText(data.tagline, { singleLine: true, maxLength: 60 });
                
                // Typewriter effect
                let currentText = '';
                for (let i = 0; i < fullText.length; i++) {
                  currentText += fullText[i];
                  updateFormData('tagline', currentText);
                  await new Promise(resolve => setTimeout(resolve, 30));
                }
                
                toast.success('✨ Tagline generated!');
              } catch (error) {
                toast.error('Failed to generate tagline');
              }
            }}
            className={`text-xs font-medium whitespace-nowrap flex items-center gap-1 ${
              aiCooldowns.tagline > 0 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-blue-600 hover:text-blue-700'
            }`}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {aiCooldowns.tagline > 0 ? aiCooldowns.tagline : 'Use AI'}
          </button>
        ) : (
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Use AI
          </span>
        )}
      </div>
    </div>
    <div>
      <Label>Description <span className="text-red-500">*</span></Label>
      <Textarea 
        value={formData.description} 
        onChange={(e) => updateFormData('description', e.target.value)} 
        placeholder="Describe your service..." 
        maxLength={2000}
        className="min-h-[100px] sm:min-h-[120px] mt-1" 
        data-testid="service-description-input"
      />
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-gray-500">Full service details • Sections added in later step</p>
        <button
          type="button"
          onClick={() => setShowAIDescriptionModal(true)}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap flex items-center gap-1"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Use AI
        </button>
      </div>
    </div>
  </div>
);

const Step2Images = ({ formData, updateFormData, handleImageUpload, removeImage, setMainImage, isUploadingImages }) => (
  <div className="space-y-4 sm:space-y-6">
    <WizardStepHeader icon={Image} tone="indigo" title="Service images" subtitle="Add photos of your service" />
    
    <div>
      <Label>Images <span className="text-gray-400 font-normal">(optional)</span> <span className="text-gray-400">{formData.images.length}/10</span></Label>
      <div className="mt-2">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
          {formData.images.map((url, i) => (
            <div key={i} className={`relative group rounded-lg overflow-hidden border ${i === formData.mainImageIndex ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'}`}>
              <img src={url} alt={`${i + 1}`} className="w-full h-24 sm:h-32 object-cover" />
              {i === formData.mainImageIndex && <div className="absolute top-1 left-1 bg-blue-600 text-white text-xs px-2 py-0.5 rounded">Main</div>}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center gap-2">
                {i !== formData.mainImageIndex && <button onClick={() => setMainImage(i)} className="opacity-0 group-hover:opacity-100 bg-blue-600 text-white px-2 py-1 rounded text-xs cursor-pointer">Set Main</button>}
                <button onClick={() => removeImage(i)} className="opacity-0 group-hover:opacity-100 bg-red-600 text-white p-1.5 rounded cursor-pointer"><X className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
          
          {formData.images.length < 10 && (
            <div className={`border border-dashed rounded-lg p-4 sm:p-6 text-center h-24 sm:h-32 flex flex-col items-center justify-center ${isUploadingImages ? 'border-blue-400 bg-blue-50' : 'border-gray-300 cursor-pointer'}`}>
              <input type="file" id="image-upload" multiple accept="image/*" onChange={handleImageUpload} className="hidden" disabled={isUploadingImages} />
              <label htmlFor="image-upload" className={`w-full h-full flex flex-col items-center justify-center ${isUploadingImages ? 'cursor-wait' : 'cursor-pointer'}`}>
                {isUploadingImages ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-1"></div>
                    <p className="text-xs font-medium text-blue-700">Uploading...</p>
                  </>
                ) : (
                  <>
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-xl mb-1">+</div>
                    <p className="text-xs font-medium text-gray-700">Upload</p>
                  </>
                )}
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

const Step3Pricing = ({ formData, updateFormData }) => {
  const isFlexible = formData.durationModel === 'flexible_dates';
  const isDayPass = formData.durationModel === 'day_pass';

  const addDuration = () => {
    const newOpts = [...formData.durationOptions, { duration: 120, price: 200, enabled: true }];
    updateFormData('durationOptions', newOpts);
  };
  
  const updateDuration = (i, field, val) => {
    const newOpts = [...formData.durationOptions];
    newOpts[i] = { ...newOpts[i], [field]: val };
    updateFormData('durationOptions', newOpts);
  };
  
  const removeDuration = (i) => {
    if (formData.durationOptions.length > 1) {
      updateFormData('durationOptions', formData.durationOptions.filter((_, idx) => idx !== i));
    }
  };

  const setFeatured = (i) => {
    updateFormData('durationOptions', formData.durationOptions.map((o, idx) => ({ ...o, featured: idx === i })));
  };

  return (
    <div className="space-y-5">
      <WizardStepHeader icon={DollarSign} tone="green" title="Pricing & Duration" subtitle="How does this booking work?" />

      {/* ── Duration Model Toggle ── */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { value: 'slot_based', label: 'Scheduled', sub: 'Time slots guests pick from', icon: Clock },
          { value: 'flexible_dates', label: 'Date range', sub: 'Guests pick start & end dates', icon: CalendarDays },
          { value: 'day_pass', label: 'Day Pass', sub: 'One ticket = whole-day access', icon: Ticket },
        ].map(opt => {
          const active = formData.durationModel === opt.value;
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              type="button"
              data-testid={`duration-model-${opt.value}`}
              onClick={() => updateFormData('durationModel', opt.value)}
              className={`text-left p-4 rounded-xl transition-all ${
                active
                  ? 'bg-blue-50 ring-1 ring-blue-500'
                  : 'bg-white shadow-[0_2px_10px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]'
              }`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${active ? 'bg-blue-100 text-blue-600' : 'bg-gray-50 text-gray-500'}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className={`text-sm font-medium ${active ? 'text-blue-700' : 'text-gray-900'}`}>{opt.label}</div>
              <div className="text-xs text-gray-500 font-light mt-0.5 leading-tight">{opt.sub}</div>
            </button>
          );
        })}
      </div>

      {/* ── Day Pass: single price (capacity comes from linked assets) ── */}
      {isDayPass ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price per pass <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 font-light mb-2">One ticket grants whole-day access — no time slot to pick.</p>
            <div className="max-w-[180px]">
              <MoneyInput
                value={formData.durationOptions[0]?.price ?? ''}
                onChange={(v) => updateDuration(0, 'price', v)}
                allowEmpty
                placeholder="49.00"
                data-testid="day-pass-price-input"
              />
            </div>
          </div>
          <div className="bg-blue-50 rounded-xl p-3.5 flex items-start gap-2.5">
            <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 font-light leading-relaxed">
              Daily capacity is set by the assets you link in the <span className="font-medium">Assets</span> step — the
              combined asset capacity is how many passes can sell per day. Once a day sells out, it's marked full.
            </p>
          </div>
        </div>
      ) : isFlexible ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Base nightly rate <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-500 font-light mb-2">Starting price per night. Add seasonal tiers in the Dynamic Pricing step.</p>
          <div className="max-w-[180px]">
            <MoneyInput
              value={formData.durationOptions[0]?.price ?? ''}
              onChange={(v) => updateDuration(0, 'price', v)}
              allowEmpty
              placeholder="150.00"
            />
          </div>
        </div>
      ) : (
        /* ── Slot-based: duration options ── */
        <div>
          <div className="space-y-3">
            {formData.durationOptions.map((opt, i) => (
              <div key={i}>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1">Price <span className="text-red-500">*</span></Label>
                    <MoneyInput
                      value={opt.price}
                      onChange={(v) => updateDuration(i, 'price', v)}
                      placeholder="150.00"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1">
                      Duration <span className="text-gray-400">(min)</span> <span className="text-red-500">*</span>
                    </Label>
                    <div className="flex gap-2">
                      <Input 
                        type="number" value={opt.duration}
                        onChange={e => updateDuration(i, 'duration', parseInt(e.target.value) || 60)} 
                        className="flex-1" placeholder="60" 
                      />
                      {formData.priceDisplayMode === 'featured_hourly' && (
                        <button
                          type="button"
                          data-testid={`duration-featured-${i}`}
                          onClick={() => setFeatured(i)}
                          title="Advertise this duration's hourly rate on your widget card"
                          className={`p-2 transition-colors ${opt.featured ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
                        >
                          <Star className={`w-4 h-4 ${opt.featured ? 'fill-amber-400' : ''}`} />
                        </button>
                      )}
                      {formData.durationOptions.length > 1 && (
                        <button type="button" onClick={() => removeDuration(i)} className="text-gray-400 hover:text-red-500 transition-colors p-2">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addDuration}
              className="w-full py-2.5 text-sm font-medium text-gray-600 border border-dashed border-gray-300 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Another Duration
            </button>

            {/* Widget card price display mode */}
            <div className="pt-2">
              <Label className="text-xs text-gray-600 mb-1.5 block">Price shown on your booking widget card</Label>
              <div className="flex flex-col gap-1.5">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="priceDisplayMode"
                    data-testid="price-display-hourly"
                    checked={(formData.priceDisplayMode || 'hourly') === 'hourly'}
                    onChange={() => updateFormData('priceDisplayMode', 'hourly')}
                  />
                  <span>Hourly rate <span className="text-gray-400">(e.g. "$158 / hour" — default)</span></span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="priceDisplayMode"
                    data-testid="price-display-from-lowest"
                    checked={formData.priceDisplayMode === 'from_lowest'}
                    onChange={() => updateFormData('priceDisplayMode', 'from_lowest')}
                  />
                  <span>Lowest duration price <span className="text-gray-400">(e.g. "From $250 / 30 min")</span></span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="priceDisplayMode"
                    data-testid="price-display-featured-hourly"
                    checked={formData.priceDisplayMode === 'featured_hourly'}
                    onChange={() => {
                      updateFormData('priceDisplayMode', 'featured_hourly');
                      const opts = formData.durationOptions || [];
                      if (opts.length && !opts.some(o => o.featured)) {
                        let bi = 0, br = Infinity;
                        opts.forEach((o, idx) => {
                          const m = o.duration || 60;
                          const r = (Number(o.price) || 0) / (m / 60);
                          if (r < br) { br = r; bi = idx; }
                        });
                        updateFormData('durationOptions', opts.map((o, idx) => ({ ...o, featured: idx === bi })));
                      }
                    }}
                  />
                  <span>Hourly rate of a featured duration <span className="text-gray-400">(mark <Star className="w-3 h-3 inline fill-amber-400 text-amber-500" /> a duration above)</span></span>
                </label>
              </div>
              {formData.priceDisplayMode === 'featured_hourly' && (() => {
                const f = (formData.durationOptions || []).find(o => o.featured && o.enabled !== false);
                if (!f || !f.duration || !f.price) return null;
                const rate = Math.round((Number(f.price) || 0) / (f.duration / 60));
                const cur = formData.currency === 'USD' || !formData.currency ? '$' : `${formData.currency} `;
                return (
                  <p className="text-xs text-gray-500 mt-1.5" data-testid="featured-rate-preview">
                    Card will show: <span className="font-medium text-gray-700">From {cur}{rate} / hour</span>
                  </p>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 5: Unavailability - Date ranges when service cannot be booked
const Step5Unavailability = ({ formData, updateFormData }) => {
  const [newSlot, setNewSlot] = useState({ startDate: '', endDate: '' });
  
  const addSlot = () => {
    if (!newSlot.startDate || !newSlot.endDate) {
      return;
    }
    
    // Validate end date is after start date
    if (new Date(newSlot.endDate) < new Date(newSlot.startDate)) {
      return;
    }
    
    const slots = formData.unavailabilitySlots || [];
    updateFormData('unavailabilitySlots', [...slots, { ...newSlot }]);
    setNewSlot({ startDate: '', endDate: '' });
  };
  
  const removeSlot = (index) => {
    const slots = formData.unavailabilitySlots || [];
    updateFormData('unavailabilitySlots', slots.filter((_, i) => i !== index));
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  const slots = formData.unavailabilitySlots || [];
  
  return (
    <div className="space-y-6">
      <WizardStepHeader icon={CalendarX} tone="rose" title="Unavailability" subtitle="Block off dates when this service can't be booked" />
      
      {/* Current Slots */}
      {slots.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">Blocked Date Ranges</Label>
          {slots.map((slot, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <CalendarX className="w-4 h-4 text-red-500" />
                <span className="text-sm font-medium text-gray-900">
                  {formatDate(slot.startDate)} — {formatDate(slot.endDate)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => removeSlot(index)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Add New Slot */}
      <div className="border border-gray-200 rounded-xl p-4 space-y-4">
        <Label className="text-sm font-medium text-gray-700">Add Unavailable Period</Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500 mb-1">Start Date</Label>
            <Input
              type="date"
              value={newSlot.startDate}
              onChange={(e) => setNewSlot({ ...newSlot, startDate: e.target.value })}
              className="text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1">End Date</Label>
            <Input
              type="date"
              value={newSlot.endDate}
              onChange={(e) => setNewSlot({ ...newSlot, endDate: e.target.value })}
              className="text-sm"
            />
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={addSlot}
          disabled={!newSlot.startDate || !newSlot.endDate}
          className="w-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Unavailable Period
        </Button>
      </div>
      
      {/* Empty State */}
      {slots.length === 0 && (
        <div className="text-center py-6 text-gray-500">
          <CalendarX className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No blocked dates</p>
          <p className="text-xs text-gray-400 mt-1">Service is available on all days with start times</p>
        </div>
      )}
    </div>
  );
};

const Step6DynamicPricing = ({ formData, updateFormData }) => {
  const locale = useOperatorLocale();
  // New unified rule form state
  const [newRule, setNewRule] = useState({ name: '', startDate: '', endDate: '', adjustment: 10 });

  const isFlexible = formData.durationModel === 'flexible_dates';

  const daysOfWeek = [
    { value: 'monday', label: 'M', fullLabel: 'Mon' },
    { value: 'tuesday', label: 'T', fullLabel: 'Tue' },
    { value: 'wednesday', label: 'W', fullLabel: 'Wed' },
    { value: 'thursday', label: 'Th', fullLabel: 'Thu' },
    { value: 'friday', label: 'F', fullLabel: 'Fri' },
    { value: 'saturday', label: 'Sa', fullLabel: 'Sat' },
    { value: 'sunday', label: 'Su', fullLabel: 'Sun' }
  ];

  const updateMultiplier = (day, value) => {
    const newMultipliers = { ...formData.pricingMultipliers, [day]: value };
    updateFormData('pricingMultipliers', newMultipliers);
    if (value !== 0) updateFormData('dynamicPricingEnabled', true);
  };

  const calculatePrice = (multiplier) => {
    const base = formData.durationOptions?.[0]?.price || formData.basePrice || 0;
    return base + base * (multiplier / 100);
  };

  // Convert YYYY-MM-DD → MM-DD (annual/repeating)
  const toMMDD = (yyyymmdd) => yyyymmdd ? yyyymmdd.slice(5) : '';

  // Convert MM-DD → readable label e.g. "Jun 14"
  const formatMMDD = (mmdd) => {
    if (!mmdd) return '';
    try {
      const [m, d] = mmdd.split('-');
      return new Date(2024, parseInt(m) - 1, parseInt(d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return mmdd; }
  };

  // Separate existing seasonalPricing into flat-rate tiers (nightlyRate set) and % rules (adjustment set)
  const pctRules = (formData.seasonalPricing || []).filter(s => s.adjustment != null && s.nightlyRate == null);

  const addPctRule = () => {
    if (!newRule.startDate) { toast.error('Select a start date'); return; }
    const start = toMMDD(newRule.startDate);
    const end = newRule.endDate ? toMMDD(newRule.endDate) : start;
    const rule = {
      seasonName: newRule.name || formatMMDD(start) + (start !== end ? ` – ${formatMMDD(end)}` : ''),
      startDate: start,
      endDate: end,
      adjustment: newRule.adjustment || 10,
      nightlyRate: null,
      weeklyRate: null,
      minNights: 1,
      enabled: true,
    };
    updateFormData('seasonalPricing', [...(formData.seasonalPricing || []), rule]);
    updateFormData('dynamicPricingEnabled', true);
    setNewRule({ name: '', startDate: '', endDate: '', adjustment: 10 });
    toast.success('Price rule added');
  };

  const removePctRule = (ruleToRemove) => {
    updateFormData('seasonalPricing', formData.seasonalPricing.filter(r => r !== ruleToRemove));
  };

  const updatePctRule = (rule, field, value) => {
    const updated = formData.seasonalPricing.map(r =>
      r === rule ? { ...r, [field]: value } : r
    );
    updateFormData('seasonalPricing', updated);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <WizardStepHeader icon={Percent} tone="amber" title="Dynamic pricing" subtitle="Adjust prices by day of week, date range, or demand" />
      
      {/* Day of Week Pricing */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Day of Week Adjustments</h4>
        <div className="border border-gray-200 rounded-lg overflow-x-auto">
          <div style={{ minWidth: 280 }}>
          {/* Header Row */}
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
            {daysOfWeek.map(day => (
              <div key={day.value} className="px-1 py-2 text-xs font-semibold text-gray-600 text-center border-r border-gray-200 last:border-r-0">
                {day.label}
              </div>
            ))}
          </div>
          
          {/* Controls Row */}
          <div className="grid grid-cols-7">
            {daysOfWeek.map(day => {
              const multiplier = formData.pricingMultipliers?.[day.value] || 0;
              
              return (
                <div key={day.value} className="flex flex-col items-center py-2 px-1 border-r border-gray-200 last:border-r-0">
                  <button
                    type="button"
                    onClick={() => updateMultiplier(day.value, Math.min(200, multiplier + 1))}
                    className="w-5 h-5 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded text-gray-700 font-bold transition-colors mb-1 text-xs"
                  >
                    +
                  </button>
                  <div className="text-xs font-semibold text-gray-900 my-0.5">
                    {multiplier}%
                  </div>
                  <button
                    type="button"
                    onClick={() => updateMultiplier(day.value, Math.max(-100, multiplier - 1))}
                    className="w-5 h-5 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded text-gray-700 font-bold transition-colors mt-1 text-xs"
                  >
                    −
                  </button>
                </div>
              );
            })}
          </div>
          
          {/* Final Prices Row */}
          <div className="grid grid-cols-7 bg-gray-50 border-t border-gray-200">
            {daysOfWeek.map(day => {
              const multiplier = formData.pricingMultipliers?.[day.value] || 0;
              const finalPrice = calculatePrice(multiplier);
              
              return (
                <div key={day.value} className="px-1 py-1.5 text-center border-r border-gray-200 last:border-r-0">
                  <p className={`text-xs font-semibold ${multiplier > 0 ? 'text-green-600' : multiplier < 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                    {locale.formatMoney(finalPrice)}
                  </p>
                </div>
              );
            })}
          </div>
          </div>{/* end minWidth wrapper */}
        </div>
      </div>
      
      {/* ── Off-Peak Duration Pricing ─────────────────────────────── */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-1">
          Off-Peak Duration Pricing
          <span className="font-normal text-gray-500 ml-2">(exact prices per duration)</span>
        </h4>
        <p className="text-xs text-gray-500 mb-3">
          Charge exact lower prices on quieter days. Pick the off-peak days, then set the discounted price for each duration. Leave blank to keep the standard rate.
        </p>
        {Array.isArray(formData.offPeakPricing?.rules) ? (
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-800" data-testid="offpeak-advanced-note">
            This service uses advanced off-peak rules (multiple day/time windows). They are preserved as-is when you save. Contact support to modify them.
          </div>
        ) : (
        <div className="border border-gray-200 rounded-lg p-3 space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Off-peak days</label>
            <div className="flex gap-1.5 flex-wrap">
              {daysOfWeek.map(day => {
                const selected = (formData.offPeakPricing?.days || []).includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    data-testid={`offpeak-day-${day.value}`}
                    onClick={() => {
                      const days = selected
                        ? (formData.offPeakPricing?.days || []).filter(d => d !== day.value)
                        : [...(formData.offPeakPricing?.days || []), day.value];
                      const prices = formData.offPeakPricing?.prices || {};
                      updateFormData('offPeakPricing', { enabled: days.length > 0 && Object.keys(prices).length > 0, days, prices });
                    }}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-semibold border transition-colors ${selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                  >
                    {day.fullLabel}
                  </button>
                );
              })}
            </div>
          </div>
          {(formData.offPeakPricing?.days || []).length > 0 && (
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">Time window <span className="text-gray-400">(optional — leave empty for all day)</span></label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  data-testid="offpeak-time-start"
                  value={formData.offPeakPricing?.timeStart || ''}
                  onChange={e => updateFormData('offPeakPricing', { ...formData.offPeakPricing, timeStart: e.target.value || undefined })}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                />
                <span className="text-xs text-gray-400">to</span>
                <input
                  type="time"
                  data-testid="offpeak-time-end"
                  value={formData.offPeakPricing?.timeEnd || ''}
                  onChange={e => updateFormData('offPeakPricing', { ...formData.offPeakPricing, timeEnd: e.target.value || undefined })}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Off-peak applies only to start times inside this window (inclusive), e.g. 12:00–12:40.</p>
            </div>
          )}
          {(formData.offPeakPricing?.days || []).length > 0 && (
            <div className="space-y-2">
              {(formData.durationOptions || []).filter(o => o.enabled !== false).map((opt, i) => {
                const mins = opt.duration || 60;
                const key = String(mins);
                const val = formData.offPeakPricing?.prices?.[key] ?? '';
                const label = mins < 60 ? `${mins} min` : `${parseFloat((mins / 60).toFixed(1))} hr`;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-sm text-gray-700 w-16 flex-shrink-0">{label}</span>
                    <span className="text-xs text-gray-400 line-through w-16 flex-shrink-0">{locale.formatMoney(opt.price || 0)}</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="Off-peak price"
                      data-testid={`offpeak-price-${mins}`}
                      value={val}
                      onChange={e => {
                        const prices = { ...(formData.offPeakPricing?.prices || {}) };
                        if (e.target.value === '') { delete prices[key]; } else { prices[key] = parseFloat(e.target.value); }
                        const days = formData.offPeakPricing?.days || [];
                        updateFormData('offPeakPricing', { ...formData.offPeakPricing, enabled: days.length > 0 && Object.keys(prices).length > 0, days, prices });
                      }}
                      className="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                    />
                    {val !== '' && (
                      <span className="text-xs font-semibold text-green-600">{locale.formatMoney(val)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>
      
      {/* ── Seasonal & Holiday Pricing ─────────────────────────────── */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-1">
          Seasonal &amp; Holiday Pricing
          <span className="font-normal text-gray-500 ml-2">(repeats annually)</span>
        </h4>
        <p className="text-xs text-gray-500 mb-3">
          Add price adjustments for any date or date range — summers, holidays, special events.
          Pick a start date (and optional end date). Rules repeat every year.
        </p>

        {/* Existing % rules */}
        {pctRules.map((rule, idx) => {
          const isSingleDay = rule.startDate === rule.endDate;
          const dateLabel = isSingleDay
            ? formatMMDD(rule.startDate)
            : `${formatMMDD(rule.startDate)} – ${formatMMDD(rule.endDate)}`;
          return (
            <div key={idx} className="flex items-center gap-2 mb-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800 truncate">{rule.seasonName || dateLabel}</div>
                <div className="text-xs text-gray-500">{dateLabel}</div>
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={rule.adjustment ?? 10}
                  onChange={e => updatePctRule(rule, 'adjustment', parseFloat(e.target.value) || 0)}
                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                />
                <span className="text-xs text-gray-500">%</span>
              </div>
              <span className={`text-xs font-semibold w-10 text-right ${rule.adjustment >= 0 ? 'text-green-600' : 'text-orange-500'}`}>
                {rule.adjustment >= 0 ? '+' : ''}{rule.adjustment}%
              </span>
              <button type="button" onClick={() => removePctRule(rule)} className="text-red-400 hover:text-red-600 ml-1 flex-shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}

        {/* Add new rule form */}
        <div className="border border-dashed border-gray-300 rounded-lg p-3">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Label <span className="text-gray-400">(optional)</span></label>
              <input
                type="text"
                placeholder="e.g. Summer, July 4th"
                value={newRule.name}
                onChange={e => setNewRule(r => ({ ...r, name: e.target.value }))}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Price adjustment</label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  placeholder="10"
                  value={newRule.adjustment}
                  onChange={e => setNewRule(r => ({ ...r, adjustment: parseFloat(e.target.value) || 0 }))}
                  className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
                <span className="text-sm text-gray-500">%</span>
                <span className={`text-xs font-medium ml-1 ${newRule.adjustment >= 0 ? 'text-green-600' : 'text-orange-500'}`}>
                  {newRule.adjustment >= 0 ? '▲ higher' : '▼ lower'}
                </span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Start date <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={newRule.startDate}
                onChange={e => setNewRule(r => ({ ...r, startDate: e.target.value }))}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">End date <span className="text-gray-400">(leave blank for single day)</span></label>
              <input
                type="date"
                value={newRule.endDate}
                min={newRule.startDate}
                onChange={e => setNewRule(r => ({ ...r, endDate: e.target.value }))}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
          {newRule.startDate && (
            <div className="text-xs text-gray-500 mb-2">
              Applies: {formatMMDD(toMMDD(newRule.startDate))}{newRule.endDate && newRule.endDate !== newRule.startDate ? ` – ${formatMMDD(toMMDD(newRule.endDate))}` : ''} · every year
            </div>
          )}
          <button
            type="button"
            onClick={addPctRule}
            disabled={!newRule.startDate}
            className="w-full py-1.5 bg-gray-800 hover:bg-gray-900 text-white rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
          >
            <PlusIcon className="w-4 h-4" /> Add Rule
          </button>
        </div>

        {pctRules.length === 0 && (
          <p className="text-xs text-gray-400 mt-2 text-center">
            Example: Summer Jun 14–Aug 14 +15% · July 4th +25% · Off-season Nov–Mar -10%
          </p>
        )}
      </div>

      {/* ── Inventory Pricing ──────────────────────────────────────── */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-1">
          Inventory Pricing
          <span className="font-normal text-gray-500 ml-2">(price rises as you fill up)</span>
        </h4>
        <p className="text-xs text-gray-500 mb-3">Automatically charge more when demand is high. Leave empty to keep a flat price.</p>

        {(formData.inventoryPricing || []).map((tier, idx) => (
          <div key={idx} className="flex items-center gap-2 mb-2">
            <span className="text-xs text-gray-500 whitespace-nowrap">When</span>
            <div className="relative w-20">
              <Input
                type="number" min="1" max="99"
                value={tier.threshold_pct || ''}
                onChange={e => {
                  const arr = [...formData.inventoryPricing];
                  arr[idx] = { ...tier, threshold_pct: parseInt(e.target.value) || 0 };
                  updateFormData('inventoryPricing', arr);
                }}
                className="pr-6 text-sm"
                placeholder="50"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">booked →</span>
            <div className="relative w-20">
              <Input
                type="number" step="0.01" min="0.5" max="5"
                value={tier.multiplier || ''}
                onChange={e => {
                  const arr = [...formData.inventoryPricing];
                  arr[idx] = { ...tier, multiplier: parseFloat(e.target.value) || 1 };
                  updateFormData('inventoryPricing', arr);
                }}
                className="text-sm"
                placeholder="1.2"
              />
            </div>
            <span className="text-xs text-gray-500">×</span>
            <span className="text-xs text-green-600 font-medium whitespace-nowrap">
              {tier.multiplier ? `+${Math.round((tier.multiplier - 1) * 100)}%` : ''}
            </span>
            <button type="button" onClick={() => {
              updateFormData('inventoryPricing', formData.inventoryPricing.filter((_, i) => i !== idx));
            }} className="text-red-400 hover:text-red-600 ml-1">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        <button type="button" onClick={() => {
          const existing = formData.inventoryPricing || [];
          const nextPct = existing.length === 0 ? 50 : existing.length === 1 ? 80 : 0;
          updateFormData('inventoryPricing', [...existing, { threshold_pct: nextPct, multiplier: 1.2 }]);
        }} className="w-full py-2 border border-dashed border-gray-300 hover:border-gray-400 rounded-lg text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center gap-1">
          <PlusIcon className="w-4 h-4"/> Add Tier
        </button>
        {(formData.inventoryPricing || []).length > 0 && (
          <p className="text-xs text-gray-400 mt-1">Example: 50% booked → 1.2× = 20% price increase</p>
        )}
      </div>

      {/* ── Time-Based Pricing ─────────────────────────────────────── */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-1">
          Time-Based Pricing
          <span className="font-normal text-gray-500 ml-2">(last-minute deals or early-bird rates)</span>
        </h4>
        <p className="text-xs text-gray-500 mb-3">Adjust price based on how far out the booking is. Use &lt;1 to discount, &gt;1 to charge more.</p>

        {(formData.timePricing || []).map((rule, idx) => (
          <div key={idx} className="flex items-center gap-2 mb-2">
            <span className="text-xs text-gray-500 whitespace-nowrap">Within</span>
            <div className="relative w-20">
              <Input
                type="number" min="1"
                value={rule.days_before || ''}
                onChange={e => {
                  const arr = [...formData.timePricing];
                  arr[idx] = { ...rule, days_before: parseInt(e.target.value) || 0 };
                  updateFormData('timePricing', arr);
                }}
                className="pr-8 text-sm"
                placeholder="7"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">d</span>
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">→</span>
            <div className="w-20">
              <Input
                type="number" step="0.01" min="0.1" max="5"
                value={rule.multiplier || ''}
                onChange={e => {
                  const arr = [...formData.timePricing];
                  arr[idx] = { ...rule, multiplier: parseFloat(e.target.value) || 1 };
                  updateFormData('timePricing', arr);
                }}
                className="text-sm"
                placeholder="0.8"
              />
            </div>
            <span className="text-xs text-gray-500">×</span>
            <span className={`text-xs font-medium whitespace-nowrap ${rule.multiplier < 1 ? 'text-orange-500' : 'text-green-600'}`}>
              {rule.multiplier ? (rule.multiplier < 1 ? `-${Math.round((1 - rule.multiplier) * 100)}%` : `+${Math.round((rule.multiplier - 1) * 100)}%`) : ''}
            </span>
            <button type="button" onClick={() => {
              updateFormData('timePricing', formData.timePricing.filter((_, i) => i !== idx));
            }} className="text-red-400 hover:text-red-600 ml-1">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        <button type="button" onClick={() => {
          const existing = formData.timePricing || [];
          updateFormData('timePricing', [...existing, { days_before: 3, multiplier: 0.85 }]);
        }} className="w-full py-2 border border-dashed border-gray-300 hover:border-gray-400 rounded-lg text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center gap-1">
          <PlusIcon className="w-4 h-4"/> Add Rule
        </button>
        {(formData.timePricing || []).length > 0 && (
          <p className="text-xs text-gray-400 mt-1">Example: Within 3 days → 0.85× = 15% last-minute discount</p>
        )}
      </div>

      {/* ── Deposit Settings ───────────────────────────────────────── */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-1">
          Secure Deposit
          <span className="font-normal text-gray-500 ml-2">(collect partial payment upfront)</span>
        </h4>
        <p className="text-xs text-gray-500 mb-3">Require a deposit to secure the booking. The remaining balance can be collected later.</p>
        
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-700">{formData.depositEnabled ? 'Deposit required' : 'No deposit (full payment)'}</span>
          <ServiceToggle
            checked={!!formData.depositEnabled}
            onChange={(checked) => updateFormData('depositEnabled', checked)}
          />
        </div>
        
        {formData.depositEnabled && (
          <div className="grid grid-cols-2 gap-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Type</label>
              <select value={formData.depositType} onChange={e => updateFormData('depositType', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white">
                <option value="percentage">Percentage of total</option>
                <option value="fixed">Fixed amount ({locale.currencySymbol})</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                {formData.depositType === 'percentage' ? 'Percentage (%)' : `Amount (${locale.currencySymbol})`}
              </label>
              <Input type="number" min={formData.depositType === 'percentage' ? 10 : 1}
                max={formData.depositType === 'percentage' ? 100 : undefined}
                value={formData.depositAmount} onChange={e => updateFormData('depositAmount', Number(e.target.value))}
                className="bg-white"/>
            </div>
            <p className="col-span-2 text-xs text-gray-500">
              {formData.depositType === 'percentage' 
                ? `Customer pays ${formData.depositAmount}% upfront to secure booking` 
                : `Customer pays ${locale.formatMoney(formData.depositAmount)} upfront to secure booking`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Step7Location = ({ formData, updateFormData, orgCountry }) => {
  const locale = useOperatorLocale();
  const taxLabel = locale.taxLabel || 'Tax';
  const lockedRate = locale.isUAE; // UAE VAT is a fixed statutory 5%

  // Auto-set country + tax type/rate from the org's locale (cannot be overridden)
  React.useEffect(() => {
    updateFormData('country', orgCountry);
    const tt = locale.isUAE ? 'vat' : locale.isUSA ? 'sales_tax' : 'tax';
    updateFormData('taxType', tt);
    if (locale.taxDefaultRate > 0 && (!formData.taxRate || formData.taxRate === 0)) {
      updateFormData('taxRate', locale.taxDefaultRate);
    }
  }, [orgCountry, locale.countryCode]);

  return (
    <div className="space-y-5">
      <WizardStepHeader icon={MapPin} tone="purple" title="Location & Tax" subtitle="Where is this service provided?" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Country - locked to org locale */}
        <div>
          <Label>Country</Label>
          <div className="w-full px-3 py-2 rounded-lg bg-gray-50 mt-1.5 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">{locale.countryName}</span>
            <span className="text-xs text-gray-400">Locked</span>
          </div>
          <p className="text-xs text-gray-500 font-light mt-1">Based on your organization&apos;s country</p>
        </div>
        <div>
          <Label>City <span className="text-red-500">*</span></Label>
          <Input
            value={formData.city}
            onChange={(e) => updateFormData('city', e.target.value)}
            placeholder={locale.isUAE ? 'Dubai' : 'Austin'}
            className="mt-1.5"
            data-testid="service-city-input"
          />
        </div>
      </div>
      <div>
        <Label>Meeting Point <span className="text-red-500">*</span></Label>
        <Input
          value={formData.locationDetails || ''}
          onChange={(e) => updateFormData('locationDetails', e.target.value)}
          placeholder={locale.isUAE ? 'Marina Dock C, Behind JBR Walk' : '1234 Lake Austin Blvd, Dock #5'}
          className="mt-1.5"
          data-testid="service-meeting-point-input"
        />
        <p className="text-xs text-gray-500 font-light mt-1">Exact address or landmark where customers should meet</p>
      </div>

      {/* ── Tax ── */}
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="min-w-0 pr-3">
            <div className="text-sm font-medium text-gray-900">Apply {taxLabel}</div>
            <div className="text-xs text-gray-500 font-light mt-0.5">
              {locale.isUAE ? 'Standard 5% VAT' : locale.isUSA ? 'Sales tax varies by state — you set the rate' : `${taxLabel} on this service`}
            </div>
          </div>
          <ServiceToggle
            checked={!!formData.taxEnabled}
            onChange={(checked) => updateFormData('taxEnabled', checked)}
          />
        </div>

        {formData.taxEnabled && (
          <div className="space-y-4 pt-4 mt-3 border-t border-gray-200/70">
            {/* Tax rate */}
            <div>
              <Label className="text-xs text-gray-600">Rate (%)</Label>
              {lockedRate ? (
                <div className="w-full px-3 py-2 rounded-lg bg-white mt-1.5 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">5%</span>
                  <span className="text-xs text-gray-400">UAE VAT (standard)</span>
                </div>
              ) : (
                <div className="relative max-w-[160px] mt-1.5">
                  <Input
                    type="number" step="0.01" min="0" max="100"
                    value={formData.taxRate}
                    onChange={(e) => updateFormData('taxRate', parseFloat(e.target.value) || 0)}
                    placeholder="8.25"
                    className="pr-8"
                  />
                  <Percent className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              )}
              {locale.isUSA && (
                <p className="text-xs text-gray-500 font-light mt-1.5">
                  You&apos;re responsible for the correct rate.{' '}
                  <a href="https://www.taxjar.com/sales-tax-calculator" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Sales tax calculator
                  </a>
                </p>
              )}
            </div>

            {/* Inclusive vs Exclusive — explicit operator choice (no default) */}
            <div>
              <Label className="text-xs text-gray-600">How is {taxLabel} applied?</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {[
                  { val: false, label: 'Added on top', sub: 'Exclusive — separate line' },
                  { val: true, label: 'Included in price', sub: 'Inclusive — already in price' },
                ].map(opt => {
                  const active = formData.taxInclusive === opt.val;
                  return (
                    <button
                      key={String(opt.val)}
                      type="button"
                      data-testid={opt.val ? 'tax-mode-inclusive' : 'tax-mode-exclusive'}
                      onClick={() => updateFormData('taxInclusive', opt.val)}
                      className={`text-left p-3 rounded-lg transition-all ${
                        active ? 'bg-blue-50 ring-1 ring-blue-500' : 'bg-white shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]'
                      }`}
                    >
                      <div className={`text-sm font-medium ${active ? 'text-blue-700' : 'text-gray-900'}`}>{opt.label}</div>
                      <div className="text-xs text-gray-500 font-light mt-0.5 leading-tight">{opt.sub}</div>
                    </button>
                  );
                })}
              </div>
              {(formData.taxInclusive === null || formData.taxInclusive === undefined) && (
                <p className="text-xs text-amber-600 mt-1.5">Choose how {taxLabel} is applied to continue.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Service Fee ── */}
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="min-w-0 pr-3">
            <div className="text-sm font-medium text-gray-900">Add a service fee</div>
            <div className="text-xs text-gray-500 font-light mt-0.5">Help offset payment processing costs with your own fee</div>
          </div>
          <ServiceToggle
            checked={!!formData.serviceFeeEnabled}
            onChange={(checked) => updateFormData('serviceFeeEnabled', checked)}
          />
        </div>

        {formData.serviceFeeEnabled && (
          <div className="space-y-4 pt-4 mt-3 border-t border-gray-200/70">
            {/* Fee Type */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { val: 'percentage', label: 'Percentage (%)' },
                { val: 'flat', label: `Flat (${locale.currencySymbol})` },
              ].map(opt => {
                const active = formData.serviceFeeType === opt.val;
                return (
                  <button
                    key={opt.val}
                    type="button"
                    onClick={() => updateFormData('serviceFeeType', opt.val)}
                    className={`p-2.5 rounded-lg text-sm font-medium transition-all ${
                      active ? 'bg-blue-50 ring-1 ring-blue-500 text-blue-700' : 'bg-white text-gray-700 shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Fee Value */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-600">
                  {formData.serviceFeeType === 'percentage' ? 'Fee percentage' : 'Fee amount'}
                </Label>
                {formData.serviceFeeType === 'percentage' ? (
                  <div className="relative mt-1.5">
                    <Input
                      type="number" step="0.01" min="0" max="10"
                      value={formData.serviceFeeValue}
                      onChange={(e) => updateFormData('serviceFeeValue', parseFloat(e.target.value) || 0)}
                      placeholder="2"
                      className="pr-8"
                    />
                    <Percent className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                ) : (
                  <div className="mt-1.5">
                    <MoneyInput
                      value={formData.serviceFeeValue}
                      onChange={(v) => updateFormData('serviceFeeValue', v)}
                      placeholder="5.00"
                    />
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs text-gray-600">Label shown to customers</Label>
                <Input
                  value={formData.serviceFeeLabel || ''}
                  onChange={(e) => updateFormData('serviceFeeLabel', e.target.value)}
                  placeholder="Service Fee"
                  className="mt-1.5"
                />
              </div>
            </div>

            {/* Preview */}
            {formData.serviceFeeValue > 0 && (
              <div className="bg-white rounded-lg p-3 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
                <p className="text-xs text-gray-500 font-light mb-0.5">Preview on a {locale.formatMoney(150)} booking</p>
                <p className="text-sm font-medium text-gray-900">
                  {formData.serviceFeeLabel || 'Service Fee'}: +{locale.formatMoney(
                    formData.serviceFeeType === 'percentage'
                      ? (150 * (formData.serviceFeeValue / 100))
                      : parseFloat(formData.serviceFeeValue || 0)
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Step 8: Safety - Emergency Contact
const Step8Safety = ({ formData, updateFormData }) => {
  const locale = useOperatorLocale();
  const defaultCountry = locale.isUAE ? 'AE' : locale.countryCode === 'GB' ? 'GB' : locale.countryCode === 'CA' ? 'CA' : 'US';

  return (
    <div className="space-y-6">
      <WizardStepHeader icon={AlertTriangle} tone="amber" title="Safety contact" subtitle="Emergency number shown on tickets and confirmation emails" />

      <div className="card-soft p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
            <Phone className="w-4 h-4" />
          </div>
          <span className="font-medium text-gray-900">Emergency contact number</span>
        </div>

        <PhoneInput
          value={formData.emergencyContactNumber || ''}
          onChange={(e164) => updateFormData('emergencyContactNumber', e164 || '')}
          defaultCountry={defaultCountry}
          placeholder="(555) 123-4567"
          data-testid="emergency-contact-phone"
        />

        <p className="text-xs text-gray-400 mt-3">Appears on customer tickets &amp; confirmation emails</p>
      </div>
    </div>
  );
};

const Step9Details = ({ formData, updateFormData, aiCooldowns, startCooldown, backendUrl }) => {
  const [newItem, setNewItem] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingItemText, setEditingItemText] = useState('');
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [editSectionTitle, setEditSectionTitle] = useState('');
  const [editSectionContent, setEditSectionContent] = useState('');
  
  const addItem = () => { if (newItem.trim()) { updateFormData('whatsIncluded', [...formData.whatsIncluded, newItem.trim()]); setNewItem(''); } };
  const removeItem = (i) => updateFormData('whatsIncluded', formData.whatsIncluded.filter((_, idx) => idx !== i));
  
  const startEditItem = (i) => {
    setEditingItemIndex(i);
    setEditingItemText(formData.whatsIncluded[i]);
  };
  
  const saveEditItem = () => {
    if (editingItemText.trim()) {
      const updated = [...formData.whatsIncluded];
      updated[editingItemIndex] = editingItemText.trim();
      updateFormData('whatsIncluded', updated);
    }
    setEditingItemIndex(null);
    setEditingItemText('');
  };
  
  const cancelEditItem = () => {
    setEditingItemIndex(null);
    setEditingItemText('');
  };
  
  const addSection = () => { 
    if (newTitle.trim() && newContent.trim()) { 
      updateFormData('customSections', [...formData.customSections, { title: newTitle.trim(), content: newContent.trim() }]);
      setNewTitle(''); 
      setNewContent(''); 
    } 
  };
  
  const startEditSection = (i) => {
    setEditingSectionIndex(i);
    setEditSectionTitle(formData.customSections[i].title);
    setEditSectionContent(formData.customSections[i].content);
  };
  
  const saveEditSection = () => {
    if (editSectionTitle.trim() && editSectionContent.trim()) {
      const updated = [...formData.customSections];
      updated[editingSectionIndex] = { title: editSectionTitle.trim(), content: editSectionContent.trim() };
      updateFormData('customSections', updated);
    }
    setEditingSectionIndex(null);
    setEditSectionTitle('');
    setEditSectionContent('');
  };
  
  const cancelEditSection = () => {
    setEditingSectionIndex(null);
    setEditSectionTitle('');
    setEditSectionContent('');
  };
  
  const removeSection = (i) => {
    updateFormData('customSections', formData.customSections.filter((_, idx) => idx !== i));
    if (editingSectionIndex === i) {
      cancelEditSection();
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <WizardStepHeader icon={FileText} tone="amber" title="Experience details" subtitle="What's included and additional info" />
      <div className="p-3 sm:p-4 border rounded-lg">
        <Label className="text-sm sm:text-base">What's Included <span className="text-red-500">*</span></Label>
        <div className="mt-2 space-y-2">
          {formData.whatsIncluded.map((item, i) => (
            <div key={i}>
              {editingItemIndex === i ? (
                <div className="flex items-center gap-2 bg-blue-50 p-2 rounded border border-blue-500">
                  <Input
                    value={editingItemText}
                    onChange={(e) => setEditingItemText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEditItem();
                      if (e.key === 'Escape') cancelEditItem();
                    }}
                    className="flex-1 text-xs sm:text-sm"
                    autoFocus
                  />
                  <button onClick={saveEditItem} className="text-green-600 hover:text-green-700 p-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                  <button onClick={cancelEditItem} className="text-gray-600 hover:text-gray-700 p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                  <span className="flex-1 text-xs sm:text-sm">{item}</span>
                  <button onClick={() => startEditItem(i)} className="text-blue-600 hover:text-blue-700 p-1" title="Edit">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); removeItem(i); }} className="text-red-600 hover:text-red-700 p-1" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addItem()} placeholder="Licensed captain, Fuel..." className="flex-1 text-sm cursor-pointer" data-testid="whats-included-input" />
            <Button type="button" onClick={addItem} size="sm" className="bg-blue-600 hover:bg-blue-700 cursor-pointer" data-testid="whats-included-add-btn">+</Button>
          </div>
        </div>
        
        {/* Use AI Button Below */}
        <div className="mt-3 flex justify-end">
          {(formData.name || formData.description) ? (
            <button
              type="button"
              disabled={aiCooldowns.inclusions > 0}
              onClick={async () => {
                if (aiCooldowns.inclusions > 0) return;
                startCooldown('inclusions'); // START IMMEDIATELY
                
                try {
                  toast.info('✨ AI is thinking...');
                  const response = await fetch(`${backendUrl}/api/ai/suggest-inclusions`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                      serviceName: formData.name,
                      tagline: formData.tagline,
                      description: formData.description
                    })
                  });
                  
                  if (!response.ok) throw new Error('Failed to generate');
                  
                  const data = await response.json();
                  const inclusions = (data.inclusions || []).map((s) => sanitizeText(s, { singleLine: true, maxLength: 100 })).filter(Boolean);
                  
                  // Typewriter effect - build locally then update
                  const finalInclusions = [];
                  
                  for (let item of inclusions) {
                    let currentText = '';
                    for (let i = 0; i < item.length; i++) {
                      currentText += item[i];
                      // Update display with partial items
                      updateFormData('whatsIncluded', [...finalInclusions, currentText]);
                      await new Promise(resolve => setTimeout(resolve, 15));
                    }
                    // Add completed item to final list
                    finalInclusions.push(item);
                    updateFormData('whatsIncluded', finalInclusions);
                    await new Promise(resolve => setTimeout(resolve, 100));
                  }
                  
                  toast.success('✨ Inclusions added!');
                } catch (error) {
                  toast.error('Failed to generate inclusions');
                }
              }}
              className={`text-xs font-medium flex items-center gap-1 ${
                aiCooldowns.inclusions > 0 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-blue-600 hover:text-blue-700'
              }`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {aiCooldowns.inclusions > 0 ? aiCooldowns.inclusions : 'Use AI'}
            </button>
          ) : (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Use AI
            </span>
          )}
        </div>
      </div>
      <div>
        <Label className="text-sm sm:text-base">Additional Sections</Label>
        <p className="text-xs text-gray-500 mb-3">Add meeting point, itinerary, what to bring, etc.</p>
        
        {formData.customSections.length > 0 && (
          <div className="space-y-2 sm:space-y-3 mb-3">
            {formData.customSections.map((s, i) => (
              <div key={i}>
                {editingSectionIndex === i ? (
                  <div className="p-3 border border-blue-400 rounded-lg bg-blue-50 space-y-2">
                    <Input 
                      value={editSectionTitle} 
                      onChange={(e) => setEditSectionTitle(e.target.value)} 
                      placeholder="Section Title"
                      className="text-sm font-semibold"
                      autoFocus
                    />
                    <Textarea 
                      value={editSectionContent} 
                      onChange={(e) => setEditSectionContent(e.target.value)} 
                      placeholder="Section content..."
                      maxLength={2000}
                      className="min-h-[80px]"
                    />
                    <div className="flex gap-2">
                      <Button onClick={saveEditSection} size="sm" className="bg-green-600 hover:bg-green-700">
                        Save
                      </Button>
                      <Button onClick={cancelEditSection} size="sm" variant="outline">
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-2 sm:p-3 border rounded-lg bg-gray-50">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="font-semibold text-sm sm:text-base">{s.title}</h4>
                      <div className="flex gap-1">
                        <button 
                          onClick={() => startEditSection(i)} 
                          className="text-blue-600 hover:text-blue-700 p-1"
                          title="Edit section"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); removeSection(i); }} 
                          className="text-red-600 hover:text-red-700 p-1"
                          title="Delete section"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-line">{s.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="p-3 sm:p-4 border border-dashed border-gray-300 rounded-lg space-y-2 sm:space-y-3">
          <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Section Title" className="text-sm cursor-pointer" />
          <Textarea value={newContent} onChange={(e) => setNewContent(e.target.value)} placeholder="Section content..." maxLength={2000} className="min-h-[80px]" />
          <Button type="button" onClick={addSection} variant="outline" className="w-full text-sm cursor-pointer">+ Add Section</Button>
        </div>
        
        {/* Use AI Button Below */}
        <div className="mt-3 flex justify-end">
          {(formData.name || formData.description) ? (
            <button
              type="button"
              disabled={aiCooldowns.sections > 0}
              onClick={async () => {
                if (aiCooldowns.sections > 0) return;
                startCooldown('sections'); // START IMMEDIATELY
                
                try {
                  toast.info('✨ AI is creating sections...');
                  const response = await fetch(`${backendUrl}/api/ai/suggest-sections`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                      serviceName: formData.name,
                      tagline: formData.tagline,
                      description: formData.description
                    })
                  });
                  
                  if (!response.ok) throw new Error('Failed to generate');
                  
                  const data = await response.json();
                  const sections = (data.sections || []).map((s) => ({
                    title: sanitizeText(s.title, { singleLine: true, maxLength: 80 }),
                    content: sanitizeText(s.content, { maxLength: 1000 }),
                  })).filter((s) => s.title || s.content);
                  
                  // Typewriter effect - build locally to avoid React state issues
                  const finalSections = [];
                  
                  for (let section of sections) {
                    let currentTitle = '';
                    let currentContent = '';
                    
                    // Type title
                    for (let i = 0; i < section.title.length; i++) {
                      currentTitle += section.title[i];
                      updateFormData('customSections', [
                        ...finalSections,
                        { title: currentTitle, content: '' }
                      ]);
                      await new Promise(resolve => setTimeout(resolve, 10));
                    }
                    
                    // Type content
                    for (let i = 0; i < section.content.length; i++) {
                      currentContent += section.content[i];
                      updateFormData('customSections', [
                        ...finalSections,
                        { title: section.title, content: currentContent }
                      ]);
                      await new Promise(resolve => setTimeout(resolve, 5));
                    }
                    
                    // Add completed section to final list
                    finalSections.push({ title: section.title, content: section.content });
                    updateFormData('customSections', finalSections);
                    await new Promise(resolve => setTimeout(resolve, 150)); // Pause between sections
                  }
                  
                  toast.success('✨ Sections created!');
                } catch (error) {
                  toast.error('Failed to generate sections');
                }
              }}
              className={`text-xs font-medium flex items-center gap-1 whitespace-nowrap ${
                aiCooldowns.sections > 0 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-blue-600 hover:text-blue-700'
              }`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {aiCooldowns.sections > 0 ? aiCooldowns.sections : 'Use AI'}
            </button>
          ) : (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Use AI
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const Step10AddOns = ({ formData, updateFormData, aiCooldowns, startCooldown, backendUrl }) => {
  const locale = useOperatorLocale();
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // Which add-on is being edited inline
  const [addOnForm, setAddOnForm] = useState({ type: 'checkbox', title: '', description: '', required: false, price: 0, percentage: 0, options: [{ label: 'Yes', price: 0 }], image: '' });
  
  const resetForm = () => { 
    setAddOnForm({ type: 'checkbox', title: '', description: '', required: false, price: 0, percentage: 0, options: [{ label: 'Yes', price: 0 }], image: '' }); 
    setEditingIndex(null); 
    setShowNewForm(false); 
  };
  
  const saveAddOn = (isNew = false) => {
    if (!addOnForm.title.trim()) { 
      toast.error('Title required'); 
      return; 
    }
    
    // Validate based on type ($0 checkboxes are allowed — e.g. "I agree" style add-ons)
    if (addOnForm.type === 'checkbox') {
      const p = Number(addOnForm.price);
      if (isNaN(p) || p < 0) {
        toast.error("Price can't be negative — use 0 for free agreement-style add-ons");
        return;
      }
    } else if (addOnForm.type === 'percentage') {
      if (!addOnForm.percentage || addOnForm.percentage <= 0) {
        toast.error('Please enter a percentage for this add-on');
        return;
      }
    } else if (addOnForm.type === 'dropdown') {
      const hasValidOption = addOnForm.options.some(opt => opt.label && opt.label.trim());
      if (!hasValidOption) {
        toast.error('Please add at least one option with a label');
        return;
      }
    }
    
    const newAddOns = [...formData.addOns];
    if (editingIndex !== null) {
      newAddOns[editingIndex] = addOnForm;
    } else {
      newAddOns.push(addOnForm);
    }
    updateFormData('addOns', newAddOns); 
    resetForm(); 
    toast.success(editingIndex !== null ? 'Updated' : 'Added');
  };
  
  const deleteAddOn = (i) => {
    if (editingIndex === i) resetForm();
    updateFormData('addOns', formData.addOns.filter((_, idx) => idx !== i));
  };
  
  const startEditAddOn = (i) => { 
    setAddOnForm(formData.addOns[i]); 
    setEditingIndex(i); 
    setShowNewForm(false);
  };
  
  const addOption = () => setAddOnForm({ ...addOnForm, options: [...addOnForm.options, { label: '', price: 0 }] });
  const updateOption = (i, field, val) => { 
    const newOpts = [...addOnForm.options]; 
    newOpts[i] = { ...newOpts[i], [field]: val }; 
    setAddOnForm({ ...addOnForm, options: newOpts }); 
  };
  const removeOption = (i) => addOnForm.options.length > 1 && setAddOnForm({ ...addOnForm, options: addOnForm.options.filter((_, idx) => idx !== i) });

  const [uploadingAddonImage, setUploadingAddonImage] = useState(false);
  const handleAddonImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAddonImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/service-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAddOnForm(prev => ({ ...prev, image: res.data.url }));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploadingAddonImage(false); }
  };

  // Inline edit form component
  const renderEditForm = () => (
    <div className="p-3 sm:p-4 border border-blue-400 rounded-lg space-y-3 sm:space-y-4 bg-blue-50">
      <div className="flex items-center justify-between">
        <Label className="text-sm sm:text-base font-semibold">{editingIndex !== null ? 'Edit Add-on' : 'New Add-on'}</Label>
        <button onClick={resetForm} className="text-gray-500 hover:text-gray-700 p-1 cursor-pointer"><X className="w-5 h-5" /></button>
      </div>
      <div>
        <Label className="text-sm">Type <span className="text-red-500">*</span></Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          {[{ value: 'checkbox', label: 'Checkbox' }, { value: 'dropdown', label: 'Dropdown' }, { value: 'percentage', label: 'Percentage' }, { value: 'informational', label: 'Info' }].map((t) => (
            <button key={t.value} onClick={() => setAddOnForm({ ...addOnForm, type: t.value })} className={`p-2.5 rounded-lg text-xs font-medium cursor-pointer transition-all ${addOnForm.type === t.value ? 'bg-blue-50 ring-1 ring-blue-500 text-blue-700' : 'bg-white text-gray-700 shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]'}`}>{t.label}</button>
          ))}
        </div>
      </div>
      <div><Label className="text-sm">Title <span className="text-red-500">*</span></Label>
        <Input value={addOnForm.title} onChange={(e) => setAddOnForm({ ...addOnForm, title: e.target.value })} placeholder="Captain's Gratuity" className="mt-1 text-sm cursor-pointer bg-white" data-testid="addon-title-input" />
      </div>
      <div><Label className="text-sm">Description</Label>
        <Textarea value={addOnForm.description} onChange={(e) => setAddOnForm({ ...addOnForm, description: e.target.value })} placeholder="Details..." maxLength={500} className="min-h-[60px] mt-1 bg-white" />
      </div>
      {addOnForm.type === 'checkbox' && (
        <div><Label className="text-sm">Price</Label>
          <MoneyInput
            value={addOnForm.price}
            onChange={(v) => setAddOnForm({ ...addOnForm, price: v })}
            className="mt-1"
            inputClassName="bg-white"
          />
        </div>
      )}
      {addOnForm.type === 'percentage' && (
        <div><Label className="text-sm">Percentage (%)</Label>
          <p className="text-xs text-gray-500 mb-2">Calculated on service price</p>
          <div className="relative mt-1"><span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
            <Input type="number" step="0.01" value={
              addOnForm.percentage > 0 
                ? (addOnForm.percentage > 1 ? addOnForm.percentage.toFixed(2) : (addOnForm.percentage * 100).toFixed(2))
                : ''
            } onChange={(e) => { 
              const val = parseFloat(e.target.value) || 0; 
              setAddOnForm({ ...addOnForm, percentage: val / 100 }); 
            }} className="pr-8 text-sm cursor-pointer bg-white" placeholder="15" />
          </div>
          <p className="text-xs text-gray-500 mt-1">Example: 15 = 15% tip</p>
        </div>
      )}
      {addOnForm.type === 'dropdown' && (
        <div><Label className="text-sm">Options <span className="text-red-500">*</span></Label>
          <div className="space-y-2 mt-2">
            {addOnForm.options.map((opt, i) => (
              <div key={i} className="flex gap-2">
                <Input value={opt.label} onChange={(e) => updateOption(i, 'label', e.target.value)} placeholder="Option" className="flex-1 text-sm cursor-pointer bg-white" />
                <div className="w-28">
                  <MoneyInput
                    value={opt.price}
                    onChange={(v) => updateOption(i, 'price', v)}
                    inputClassName="bg-white"
                  />
                </div>
                {addOnForm.options.length > 1 && <button onClick={() => removeOption(i)} className="text-red-600 p-1 cursor-pointer"><X className="w-4 h-4" /></button>}
              </div>
            ))}
            <Button type="button" onClick={addOption} variant="outline" size="sm" className="w-full text-xs cursor-pointer">+ Add Option</Button>
          </div>
        </div>
      )}
      {addOnForm.type !== 'informational' && (
        <div className="flex items-center gap-2">
          <input type="checkbox" id="required-toggle" checked={addOnForm.required} onChange={(e) => setAddOnForm({ ...addOnForm, required: e.target.checked })} className="w-4 h-4 cursor-pointer" />
          <label htmlFor="required-toggle" className="text-xs sm:text-sm font-medium cursor-pointer">Required</label>
        </div>
      )}
      {/* Optional Add-on Image */}
      <div>
        <Label className="text-sm">Photo <span className="text-gray-400 font-normal">(optional)</span></Label>
        {addOnForm.image ? (
          <div className="mt-1 flex items-center gap-2">
            <img src={addOnForm.image} alt="" className="w-16 h-16 rounded-lg object-cover border border-gray-200" />
            <button type="button" onClick={() => setAddOnForm({ ...addOnForm, image: '' })} className="text-red-500 hover:text-red-700 text-xs cursor-pointer">Remove</button>
          </div>
        ) : (
          <div className="mt-1">
            <input type="file" id="addon-image-upload" accept="image/*" onChange={handleAddonImageUpload} className="hidden" disabled={uploadingAddonImage} />
            <label htmlFor="addon-image-upload" className={`inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white hover:border-blue-400 ${uploadingAddonImage ? 'cursor-wait opacity-60' : 'cursor-pointer'}`}>
              <Image className="w-3.5 h-3.5" />
              {uploadingAddonImage ? 'Uploading...' : 'Add Photo'}
            </label>
          </div>
        )}
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="button" onClick={() => saveAddOn(editingIndex === null)} className="flex-1 text-sm bg-blue-600 hover:bg-blue-700 cursor-pointer" data-testid="addon-save-btn">{editingIndex !== null ? 'Update' : 'Add'}</Button>
        <Button type="button" onClick={resetForm} variant="outline" className="text-sm cursor-pointer" data-testid="addon-cancel-btn">Cancel</Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <WizardStepHeader icon={PlusIcon} tone="indigo" title="Add-ons" subtitle="Optional extras customers can add" />
      
      {/* List of Add-ons with inline editing */}
      {formData.addOns.length > 0 && (
        <div className="space-y-2 sm:space-y-3">
          {formData.addOns.map((a, i) => (
            <div key={i}>
              {editingIndex === i ? (
                // Inline edit form for this add-on
                renderEditForm()
              ) : (
                // Display mode
                <div className="p-3 border rounded-lg bg-white">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex gap-3 flex-1">
                      {a.image && (
                        <img src={a.image} alt="" className="w-12 h-12 rounded-lg object-cover border border-gray-200 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded font-medium">
                          {a.type === 'percentage' ? 'Percentage' : a.type.charAt(0).toUpperCase() + a.type.slice(1)}
                        </span>
                        {a.required && <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Required</span>}
                      </div>
                      
                      <div className="flex items-baseline justify-between mb-1">
                        <h4 className="font-semibold text-sm">{a.title}</h4>
                        {a.type === 'checkbox' && a.price > 0 && (
                          <span className="text-sm font-bold text-green-700">+{locale.formatMoney(a.price)}</span>
                        )}
                        {a.type === 'percentage' && a.percentage > 0 && (
                          <span className="text-sm font-bold text-green-700">
                            {a.percentage > 1 ? a.percentage.toFixed(0) : (a.percentage * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      
                      {a.description && <p className="text-xs text-gray-600 mb-2">{a.description}</p>}
                      
                      {a.type === 'dropdown' && a.options && a.options.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {a.options.map((opt, idx) => (
                            <div key={idx} className="flex justify-between text-xs text-gray-600 pl-3">
                              <span>• {opt.label}</span>
                              <span className="font-semibold text-green-700">+{locale.formatMoney(opt.price)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => startEditAddOn(i)} className="text-blue-600 hover:text-blue-700 px-2 py-1 text-xs cursor-pointer">Edit</button>
                      <button onClick={() => deleteAddOn(i)} className="text-red-600 hover:text-red-700 p-1 cursor-pointer" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* New Add-on Form (shown at bottom when adding new) */}
      {showNewForm && editingIndex === null && renderEditForm()}
      
      {/* Use AI Button */}
      {!showNewForm && editingIndex === null && (
        <div className="flex justify-end">
          {(formData.name || formData.description) ? (
            <button
              type="button"
              disabled={aiCooldowns.addons > 0}
              onClick={async () => {
                if (aiCooldowns.addons > 0) return;
                startCooldown('addons'); // START IMMEDIATELY
                
                try {
                  toast.info('✨ AI is suggesting add-ons...');
                  const response = await fetch(`${backendUrl}/api/ai/suggest-addons`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                      serviceName: formData.name,
                      tagline: formData.tagline,
                      description: formData.description
                    })
                  });
                  
                  if (!response.ok) throw new Error('Failed to generate');
                  
                  const data = await response.json();
                  const addons = (data.addOns || []).map((a) => ({
                    ...a,
                    title: sanitizeText(a.title, { singleLine: true, maxLength: 80 }),
                    description: sanitizeText(a.description, { maxLength: 300 }),
                  }));
                  
                  // Typewriter effect for each add-on
                  const finalAddOns = [];
                  
                  for (let addon of addons) {
                    let currentTitle = '';
                    let currentDescription = '';
                    
                    // Type title
                    for (let i = 0; i < addon.title.length; i++) {
                      currentTitle += addon.title[i];
                      updateFormData('addOns', [
                        ...finalAddOns,
                        { ...addon, title: currentTitle, description: '' }
                      ]);
                      await new Promise(resolve => setTimeout(resolve, 15));
                    }
                    
                    // Type description
                    if (addon.description) {
                      for (let i = 0; i < addon.description.length; i++) {
                        currentDescription += addon.description[i];
                        updateFormData('addOns', [
                          ...finalAddOns,
                          { ...addon, title: addon.title, description: currentDescription }
                        ]);
                        await new Promise(resolve => setTimeout(resolve, 8));
                      }
                    }
                    
                    // Add completed add-on
                    finalAddOns.push(addon);
                    updateFormData('addOns', finalAddOns);
                  }
                  
                  toast.success('✨ Add-ons created!');
                } catch (error) {
                  toast.error('Failed to generate add-ons');
                }
              }}
              className={`text-xs font-medium flex items-center gap-1 ${
                aiCooldowns.addons > 0 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-blue-600 hover:text-blue-700'
              }`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {aiCooldowns.addons > 0 ? aiCooldowns.addons : 'Use AI'}
            </button>
          ) : (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Use AI
            </span>
          )}
        </div>
      )}
      
      {/* Add New Button - only show when not editing */}
      {!showNewForm && editingIndex === null && (
        <Button type="button" onClick={() => setShowNewForm(true)} variant="outline" className="w-full text-sm cursor-pointer" data-testid="addon-add-new-btn">+ Add New Add-on</Button>
      )}
    </div>
  );
};

// Step 9: Custom Questions
const Step11CustomQuestions = ({ formData, updateFormData }) => {
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    type: 'text', // text, select, multiselect
    required: false,
    options: []
  });
  const [showForm, setShowForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [newOption, setNewOption] = useState('');

  const resetForm = () => {
    setNewQuestion({ question: '', type: 'text', required: false, options: [] });
    setShowForm(false);
    setEditIndex(null);
    setNewOption('');
  };

  const saveQuestion = () => {
    if (!newQuestion.question.trim()) {
      toast.error('Please enter a question');
      return;
    }
    if (newQuestion.type !== 'text' && newQuestion.options.length === 0) {
      toast.error('Please add at least one option for select/multiselect');
      return;
    }
    
    const questions = [...(formData.customQuestions || [])];
    if (editIndex !== null) {
      questions[editIndex] = newQuestion;
    } else {
      questions.push(newQuestion);
    }
    updateFormData('customQuestions', questions);
    resetForm();
    toast.success(editIndex !== null ? 'Question updated!' : 'Question added!');
  };

  const deleteQuestion = (index) => {
    const questions = formData.customQuestions.filter((_, i) => i !== index);
    updateFormData('customQuestions', questions);
    toast.success('Question removed');
  };

  const editQuestion = (index) => {
    setNewQuestion(formData.customQuestions[index]);
    setEditIndex(index);
    setShowForm(true);
  };

  const addOption = () => {
    if (!newOption.trim()) return;
    setNewQuestion({
      ...newQuestion,
      options: [...newQuestion.options, newOption.trim()]
    });
    setNewOption('');
  };

  const removeOption = (index) => {
    setNewQuestion({
      ...newQuestion,
      options: newQuestion.options.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <WizardStepHeader icon={HelpCircle} tone="purple" title="Custom questions" subtitle="Ask customers specific questions during booking" />

      {/* Existing questions list */}
      {formData.customQuestions && formData.customQuestions.length > 0 && (
        <div className="space-y-2">
          {formData.customQuestions.map((q, i) => (
            <div key={i} className="p-3 border rounded-lg bg-gray-50">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-medium capitalize">
                      {q.type === 'text' ? 'Text Input' : q.type === 'select' ? 'Dropdown' : 'Multi-Select'}
                    </span>
                    {q.required && <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Required</span>}
                  </div>
                  <h4 className="font-semibold text-sm">{q.question}</h4>
                  {q.options && q.options.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {q.options.map((opt, idx) => (
                        <span key={idx} className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded">
                          {opt}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => editQuestion(i)} className="text-blue-600 hover:text-blue-700 px-2 py-1 text-xs cursor-pointer">Edit</button>
                  <button onClick={() => deleteQuestion(i)} className="text-red-600 hover:text-red-700 p-1 cursor-pointer">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      {!showForm ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowForm(true)}
          className="w-full border-dashed text-sm cursor-pointer"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Question
        </Button>
      ) : (
        <div className="border rounded-lg p-4 space-y-4 bg-white">
          <div>
            <Label className="text-sm">Question <span className="text-red-500">*</span></Label>
            <Input
              value={newQuestion.question}
              onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
              placeholder="e.g., Do you have any dietary restrictions?"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-sm">Answer Type</Label>
            <select
              value={newQuestion.type}
              onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value, options: e.target.value === 'text' ? [] : newQuestion.options })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-1"
            >
              <option value="text">Text Input (free text)</option>
              <option value="select">Dropdown (single choice)</option>
              <option value="multiselect">Multi-Select (multiple choices)</option>
            </select>
          </div>

          {/* Options for select/multiselect */}
          {newQuestion.type !== 'text' && (
            <div>
              <Label className="text-sm">Options <span className="text-red-500">*</span></Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  placeholder="Add option"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                />
                <Button type="button" onClick={addOption} variant="outline" className="cursor-pointer">
                  <PlusIcon className="w-4 h-4" />
                </Button>
              </div>
              {newQuestion.options.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newQuestion.options.map((opt, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm">
                      {opt}
                      <button onClick={() => removeOption(idx)} className="text-red-500 hover:text-red-700 cursor-pointer">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="question-required"
              checked={newQuestion.required}
              onChange={(e) => setNewQuestion({ ...newQuestion, required: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 cursor-pointer"
            />
            <label htmlFor="question-required" className="text-sm font-medium cursor-pointer">Required</label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="button" onClick={saveQuestion} className="flex-1 text-sm bg-purple-600 hover:bg-purple-700 cursor-pointer">
              {editIndex !== null ? 'Update' : 'Add Question'}
            </Button>
            <Button type="button" onClick={resetForm} variant="outline" className="text-sm cursor-pointer">Cancel</Button>
          </div>
        </div>
      )}

      {/* Helpful tip */}
      <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
        <div className="flex items-start gap-2">
          <Lightbulb className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-purple-800">
            <p className="font-medium mb-1">Examples of useful questions:</p>
            <ul className="list-disc pl-4 space-y-0.5 text-purple-700">
              <li>What is your experience level?</li>
              <li>Do you have any medical conditions?</li>
              <li>How did you hear about us?</li>
              <li>Any special requests or celebrations?</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const Step12Policies = ({ formData, updateFormData }) => (
  <div className="space-y-4 sm:space-y-6">
    <WizardStepHeader icon={FileText} tone="rose" title="Policies & legal" subtitle="Cancellations and disclaimers" />
    <div>
      <Label className="text-sm sm:text-base">Cancellation Policy <span className="text-red-500">*</span></Label>
      <Textarea value={formData.customCancellationPolicy} onChange={(e) => updateFormData('customCancellationPolicy', e.target.value)} placeholder="Full refund 10+ days before&#10;50% refund 5-10 days before&#10;No refund within 48 hours&#10;Weather exceptions apply" maxLength={2000} className="min-h-[120px] mt-1" data-testid="cancellation-policy-input" />
    </div>
    <div>
      <Label className="text-sm sm:text-base">Disclaimers <span className="text-red-500">*</span></Label>
      <Textarea value={formData.disclaimers} onChange={(e) => updateFormData('disclaimers', e.target.value)} placeholder="No diving. Swim at your own risk.&#10;Damage policy: Up to $500 charge.&#10;Weather policy: Full refund if unsafe." maxLength={2000} className="min-h-[100px] mt-1" data-testid="disclaimers-input" />
      <p className="text-xs text-gray-600">
        Customers sign waiver on their booking confirmation. Configure waiver in{' '}
        <button 
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'waivers' }))}
          className="text-blue-600 hover:text-blue-700 cursor-pointer"
        >
          Waivers tab
        </button>
      </p>
    </div>
  </div>
);

const Step13Assets = ({ formData, updateFormData, assets }) => {
  const availableAssets = assets.filter(a => a.active !== false);
  
  // Debug logging
  
  const handleAssetToggle = (assetId) => {
    const currentIds = formData.assetIds || [];
    const newIds = currentIds.includes(assetId) 
      ? currentIds.filter(id => id !== assetId) 
      : [...currentIds, assetId];
    
    updateFormData('assetIds', newIds);
    
    // Auto-calculate capacity
    const selectedAssets = availableAssets.filter(a => newIds.includes(a.id));
    const totalCapacity = selectedAssets.reduce((sum, a) => sum + (parseInt(a.capacity, 10) || 12), 0);
    updateFormData('maxGroupSize', totalCapacity || 12);
  };
  
  const selectedAssets = availableAssets.filter(a => (formData.assetIds || []).includes(a.id));
  const calculatedCapacity = selectedAssets.reduce((sum, a) => sum + (parseInt(a.capacity, 10) || 12), 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <WizardStepHeader icon={Layers} tone="green" title="Link assets" subtitle="Select which assets provide this service" />
      <div>
        <Label className="text-sm sm:text-base">Link Assets <span className="text-red-500">*</span></Label>
        <p className="text-xs text-gray-500 mb-2">Select which assets provide this service</p>
        {availableAssets.length === 0 ? (
          <button
            type="button"
            onClick={() => {
              window.dispatchEvent(new CustomEvent('navigate', { detail: 'assets' }));
            }}
            className="w-full mt-2 p-6 border border-dashed border-gray-300 rounded-lg text-center hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer"
          >
            <p className="text-sm text-gray-600 font-medium">No assets available</p>
            <p className="text-xs text-blue-600 mt-2 hover:underline">Click here to add assets first →</p>
          </button>
        ) : (
          <div className="space-y-2">
            {availableAssets.map(a => {
              const isSelected = (formData.assetIds || []).includes(a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  data-testid={`asset-toggle-${a.id}`}
                  className={`w-full flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border cursor-pointer transition-all text-left ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-white hover:border-gray-400'}`}
                  onClick={() => handleAssetToggle(a.id)}
                >
                  <input 
                    type="checkbox" 
                    checked={isSelected} 
                    readOnly
                    className="w-4 h-4 pointer-events-none" 
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{a.name}</div>
                    <div className="text-xs text-gray-600">Capacity: {a.capacity || 12}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {formData.assetIds.length > 0 && (
        <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between"><div><p className="text-xs sm:text-sm font-medium text-green-900">Total Capacity (Auto-calculated)</p><p className="text-xs text-green-700 mt-0.5">{selectedAssets.length} asset{selectedAssets.length !== 1 ? 's' : ''} selected</p></div>
            <div className="text-2xl font-bold text-green-700">{calculatedCapacity}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const Step14Settings = ({ formData, updateFormData, orgCountry }) => {
  const countryConfig = getCountryConfig(orgCountry);
  const countryTimezones = countryConfig.timezones || [];
  
  // Get all other timezones (not from this country)
  const otherTimezones = TIMEZONES.filter(
    tz => !countryTimezones.some(ctz => ctz.value === tz.value)
  );
  
  return (
    <div className="space-y-4 sm:space-y-6">
      <WizardStepHeader icon={Settings} tone="purple" title="Booking settings" subtitle="Configure how this service is booked" />
      <div className="flex items-center justify-between p-4 rounded-xl bg-white shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
        <div className="pr-4">
          <div className="font-medium text-sm text-gray-900">Include in email recommendations</div>
          <div className="text-xs text-gray-500 font-light mt-0.5">Show this service in "you might also like" cards on abandoned-cart and post-trip emails. Turn off for internal or on-dock services.</div>
        </div>
        <button
          type="button"
          data-testid="service-include-in-recs-toggle"
          onClick={() => updateFormData('includeInRecommendations', formData.includeInRecommendations === false)}
          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 cursor-pointer ${formData.includeInRecommendations !== false ? 'bg-blue-600' : 'bg-gray-300'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.includeInRecommendations !== false ? 'translate-x-5' : ''}`} />
        </button>
      </div>
      <div>
        <Label className="text-sm sm:text-base">Booking Type</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mt-2">
          <button 
            onClick={() => updateFormData('bookingType', 'per_ticket')} 
            className={`p-4 rounded-xl text-left transition-all cursor-pointer ${formData.bookingType === 'per_ticket' ? 'bg-blue-50 ring-1 ring-blue-500' : 'bg-white shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]'}`}
          >
            <div className={`font-medium text-sm ${formData.bookingType === 'per_ticket' ? 'text-blue-700' : 'text-gray-900'}`}>Per Ticket</div>
            <div className="text-xs text-gray-500 font-light mt-0.5">Sell individual seats</div>
          </button>
          <button 
            onClick={() => updateFormData('bookingType', 'exclusive')} 
            className={`p-4 rounded-xl text-left transition-all cursor-pointer ${formData.bookingType === 'exclusive' ? 'bg-blue-50 ring-1 ring-blue-500' : 'bg-white shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]'}`}
          >
            <div className={`font-medium text-sm ${formData.bookingType === 'exclusive' ? 'text-blue-700' : 'text-gray-900'}`}>Per Asset</div>
            <div className="text-xs text-gray-500 font-light mt-0.5">Sell whole asset(s)</div>
          </button>
        </div>
        <div className="text-xs text-gray-500 mt-2 space-y-1">
          <p><span className="font-medium text-gray-700">Per Ticket:</span> Multiple groups share (e.g., 4 guests book 4 seats on a boat)</p>
          <p><span className="font-medium text-gray-700">Per Asset:</span> Private charter (e.g., group books entire boat, or multiple boats)</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <Label className="text-sm">Service Timezone</Label>
          <select value={formData.timezone} onChange={(e) => updateFormData('timezone', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-1 cursor-pointer">
            {/* Show country's timezones first with label */}
            {countryTimezones.length > 0 && (
              <optgroup label={`${countryConfig.flag} ${countryConfig.name} Timezones`}>
                {countryTimezones.map(tz => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </optgroup>
            )}
            {/* Then show all other timezones */}
            <optgroup label="Other Timezones">
              {otherTimezones.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </optgroup>
          </select>
          <p className="text-xs text-gray-500 mt-1">What timezone are your service times in?</p>
        </div>
        <div><Label className="text-sm">Minimum Notice <span className="text-gray-400">(hours)</span></Label>
          <Input type="number" value={formData.minimumNotice} onChange={(e) => updateFormData('minimumNotice', parseInt(e.target.value) || 0)} placeholder="2" className="mt-1 text-sm cursor-pointer" />
        </div>
      </div>
      
      {/* Who Pays Fees? */}
      <div className="pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <Label className="text-sm sm:text-base font-semibold">Who Pays Fees?</Label>
          <div className="group relative">
            <svg className="w-4 h-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <div className="absolute bottom-6 left-0 w-72 bg-gray-900 text-white text-xs p-3 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 leading-relaxed">
              <p className="font-semibold mb-1">How fees work:</p>
              <p>BookingFlows charges 3% of the service price. Stripe charges 2.9% + $0.30 per transaction.</p>
              <p className="mt-1">If you have partners/affiliates, their share is collected via the application fee in Stripe and transferred automatically.</p>
              <p className="mt-1 text-yellow-300">The "BookingFlows application fee" in Stripe includes both the platform fee AND any partner payouts.</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          <button 
            onClick={() => updateFormData('feeStructure', 'customer_pays')} 
            className={`p-4 rounded-xl text-left transition-all cursor-pointer ${(formData.feeStructure || 'customer_pays') === 'customer_pays' ? 'bg-blue-50 ring-1 ring-blue-500' : 'bg-white shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]'}`}
          >
            <div className={`font-medium text-sm ${(formData.feeStructure || 'customer_pays') === 'customer_pays' ? 'text-blue-700' : 'text-gray-900'}`}>Customer Pays</div>
            <div className="text-xs text-gray-500 font-light mt-0.5">Fees added as a line item at checkout. Your service price is what you earn.</div>
          </button>
          <button 
            onClick={() => updateFormData('feeStructure', 'operator_absorbs')} 
            className={`p-4 rounded-xl text-left transition-all cursor-pointer ${formData.feeStructure === 'operator_absorbs' ? 'bg-blue-50 ring-1 ring-blue-500' : 'bg-white shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]'}`}
          >
            <div className={`font-medium text-sm ${formData.feeStructure === 'operator_absorbs' ? 'text-blue-700' : 'text-gray-900'}`}>I&apos;ll Absorb Fees</div>
            <div className="text-xs text-gray-500 font-light mt-0.5">No extra charges for customers. Fees deducted from your payout.</div>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {(formData.feeStructure || 'customer_pays') === 'customer_pays' 
            ? 'Customers will see a "Service & Processing Fee" line item at checkout.'
            : 'Customers pay only the service price + tax. Platform (3%) and Stripe (~2.9% + $0.30) fees come from your revenue.'}
        </p>
      </div>
    </div>
  );
};

const Step4Availability = ({ formData, updateFormData }) => {
  const isFlexible = formData.durationModel === 'flexible_dates';
  const isDayPass = formData.durationModel === 'day_pass';

  // ─── DAY PASS branch ───────────────────────────────────────────────────────
  if (isDayPass) {
    const daysOfWeek = [
      { value: 'monday', label: 'Mon' },
      { value: 'tuesday', label: 'Tue' },
      { value: 'wednesday', label: 'Wed' },
      { value: 'thursday', label: 'Thu' },
      { value: 'friday', label: 'Fri' },
      { value: 'saturday', label: 'Sat' },
      { value: 'sunday', label: 'Sun' },
    ];
    const selected = formData.dayPassDays || [];
    const toggleDay = (day) => {
      const next = selected.includes(day) ? selected.filter(d => d !== day) : [...selected, day];
      updateFormData('dayPassDays', next);
    };
    return (
      <div className="space-y-5">
        <WizardStepHeader icon={CalendarDays} tone="blue" title="Operating days" subtitle="Which days are passes valid & on sale?" />
        <div className="flex flex-wrap gap-2">
          {daysOfWeek.map(d => {
            const active = selected.includes(d.value);
            return (
              <button
                key={d.value}
                type="button"
                data-testid={`day-pass-day-${d.value}`}
                onClick={() => toggleDay(d.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  active
                    ? 'bg-blue-600 text-white shadow-[0_8px_24px_rgba(0,0,0,0.04)]'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {d.label}
              </button>
            );
          })}
        </div>
        <div className="bg-blue-50 rounded-xl p-3.5 flex items-start gap-2.5">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700 font-light leading-relaxed">
            Passes are sold for any open day. Guests get all-day access — there are no time slots to choose.
          </p>
        </div>
      </div>
    );
  }

  // ─── FLEXIBLE DATES branch ─────────────────────────────────────────────────
  if (isFlexible) {
    const generateTimeOptions = () => {
      const opts = [];
      for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 30) {
          const val = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
          const hr12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
          const ampm = h < 12 ? 'AM' : 'PM';
          opts.push({ value: val, label: `${hr12}:${String(m).padStart(2,'0')} ${ampm}` });
        }
      }
      return opts;
    };
    const timeOpts = generateTimeOptions();

    return (
      <div className="space-y-5">
        <WizardStepHeader icon={CalendarIcon} tone="blue" title="Check-in & Check-out" subtitle="Standard times guests arrive and leave" />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Check-in time</label>
            <select
              value={formData.checkInTime || '15:00'}
              onChange={e => updateFormData('checkInTime', e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            >
              {timeOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Check-out time</label>
            <select
              value={formData.checkOutTime || '11:00'}
              onChange={e => updateFormData('checkOutTime', e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            >
              {timeOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Min stay <span className="text-gray-400 font-normal">(nights)</span></label>
            <Input
              type="number" min="1"
              value={formData.minStayNights || ''}
              onChange={e => updateFormData('minStayNights', e.target.value ? parseInt(e.target.value) : null)}
              placeholder="1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Max stay <span className="text-gray-400 font-normal">(nights, optional)</span></label>
            <Input
              type="number" min="1"
              value={formData.maxStayNights || ''}
              onChange={e => updateFormData('maxStayNights', e.target.value ? parseInt(e.target.value) : null)}
              placeholder="No limit"
            />
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-3.5 flex items-start gap-2.5">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700 font-light leading-relaxed">
            Guests pick any check-in → check-out date range. Seasonal rates from the Dynamic Pricing step apply automatically.
          </p>
        </div>
      </div>
    );
  }

  // ─── SLOT-BASED branch (original code, untouched) ──────────────────────────
  const [selectedTimes, setSelectedTimes] = useState([]);
  
  const daysOfWeek = [
    { value: 'monday', label: 'M', fullLabel: 'Mon' },
    { value: 'tuesday', label: 'T', fullLabel: 'Tue' },
    { value: 'wednesday', label: 'W', fullLabel: 'Wed' },
    { value: 'thursday', label: 'Th', fullLabel: 'Thu' },
    { value: 'friday', label: 'F', fullLabel: 'Fri' },
    { value: 'saturday', label: 'Sa', fullLabel: 'Sat' },
    { value: 'sunday', label: 'Su', fullLabel: 'Sun' }
  ];
  
  // Convert 24-hour to 12-hour format
  const formatTime12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${period}`;
  };
  
  // Generate 15-minute increment time options
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const label = formatTime12Hour(timeStr);
        options.push({ value: timeStr, label });
      }
    }
    return options;
  };
  
  const timeOptions = generateTimeOptions();
  
  const toggleTime = (time) => {
    setSelectedTimes(prev => 
      prev.includes(time) 
        ? prev.filter(t => t !== time) 
        : [...prev, time]
    );
  };
  
  // Get all unique times from availableTimeSlots
  const getConfiguredTimes = () => {
    const slots = formData.availableTimeSlots || [];
    const uniqueTimes = [...new Set(slots.map(s => s.time))];
    return uniqueTimes.sort();
  };
  
  const configuredTimes = getConfiguredTimes();
  
  // Check if a specific time+day combo exists
  const isSlotActive = (time, day) => {
    const slots = formData.availableTimeSlots || [];
    return slots.some(slot => slot.time === time && slot.days.includes(day));
  };
  
  // Toggle a specific time+day combo
  const toggleSlot = (time, day) => {
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const currentSlots = formData.availableTimeSlots || [];
    
    // Find if this time exists
    const timeSlotIndex = currentSlots.findIndex(s => s.time === time);
    
    if (timeSlotIndex >= 0) {
      const timeSlot = currentSlots[timeSlotIndex];
      
      if (timeSlot.days.includes(day)) {
        // Remove this day
        const newDays = timeSlot.days.filter(d => d !== day);
        
        if (newDays.length === 0) {
          // Remove entire time slot if no days left
          currentSlots.splice(timeSlotIndex, 1);
        } else {
          currentSlots[timeSlotIndex] = { ...timeSlot, days: newDays };
        }
      } else {
        // Add this day
        const newDays = [...timeSlot.days, day].sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
        currentSlots[timeSlotIndex] = { ...timeSlot, days: newDays };
      }
    }
    
    const updatedSlots = currentSlots.sort((a, b) => a.time.localeCompare(b.time));
    updateFormData('availableTimeSlots', updatedSlots);
  };
  
  // Add new time rows with NO days selected
  const addTimeRows = () => {
    if (selectedTimes.length === 0) {
      toast.error('Please select at least one time');
      return;
    }
    
    const currentSlots = formData.availableTimeSlots || [];
    let addedCount = 0;
    
    selectedTimes.forEach(time => {
      const exists = currentSlots.some(s => s.time === time);
      if (!exists) {
        currentSlots.push({
          time,
          days: [] // Start with NO days selected
        });
        addedCount++;
      }
    });
    
    if (addedCount === 0) {
      toast.error('All selected times already exist');
      return;
    }
    
    const updatedSlots = currentSlots.sort((a, b) => a.time.localeCompare(b.time));
    updateFormData('availableTimeSlots', updatedSlots);
    setSelectedTimes([]);
    toast.success(`${addedCount} time${addedCount > 1 ? 's' : ''} added. Click checkboxes to set days.`);
  };
  
  // Remove entire time row
  const removeTimeRow = (time) => {
    const currentSlots = formData.availableTimeSlots || [];
    updateFormData('availableTimeSlots', currentSlots.filter(s => s.time !== time));
  };
  
  return (
    <div className="space-y-4 sm:space-y-6">
      <WizardStepHeader icon={Clock} tone="blue" title="Start Times" subtitle="When can customers book?" />
      
      <div>
        <Label className="text-sm sm:text-base">Available Start Times <span className="text-red-500">*</span></Label>
        <p className="text-xs text-gray-500 mb-3">Select the times customers can start their booking. We&apos;ll handle the rest based on duration.</p>
        
        {/* Add Time Slot */}
        <div className="mb-4">
          <CustomDropdown
            multiSelect={true}
            selectedValues={selectedTimes}
            onChange={toggleTime}
            options={timeOptions}
            placeholder="Select times..."
            onDone={addTimeRows}
            testId="start-times-dropdown"
            doneButtonText={selectedTimes.length > 0 ? `+ Add ${selectedTimes.length} Time${selectedTimes.length > 1 ? 's' : ''}` : '+ Add'}
          />
        </div>
        
        {/* Time Slots Grid */}
        {configuredTimes.length > 0 ? (
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <div style={{ minWidth: 360 }}>
            {/* Header Row */}
            <div className="flex items-center bg-gray-50 border-b border-gray-200">
              <div className="flex-1 grid grid-cols-8">
                <div className="px-2 py-2 text-xs font-semibold text-gray-600">Time</div>
                {daysOfWeek.map(day => (
                  <div key={day.value} className="px-1 py-2 text-xs font-semibold text-gray-600 text-center">
                    {day.label}
                  </div>
                ))}
              </div>
              <div className="w-12"></div>
            </div>
            
            {/* Time Rows */}
            {configuredTimes.map((time) => (
              <div key={time} className="flex items-stretch border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors group">
                <div className="flex-1 grid grid-cols-8">
                  <div className="px-2 py-2.5 text-xs font-semibold text-gray-900">
                    {formatTime12Hour(time)}
                  </div>
                  {daysOfWeek.map(day => (
                    <div 
                      key={day.value} 
                      data-testid={`slot-day-${time}-${day.value}`}
                      className="flex items-center justify-center p-2 cursor-pointer hover:bg-blue-50 transition-colors"
                      onClick={() => toggleSlot(time, day.value)}
                      title={`${formatTime12Hour(time)} on ${day.fullLabel}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                        isSlotActive(time, day.value) 
                          ? 'bg-blue-600 border-blue-600' 
                          : 'border-gray-300 bg-white hover:border-blue-400'
                      }`}>
                        {isSlotActive(time, day.value) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div 
                  className="w-12 flex items-center justify-center cursor-pointer hover:bg-red-50 transition-colors"
                  onClick={() => removeTimeRow(time)}
                  title="Remove this time"
                >
                  <Trash2 className="w-4 h-4 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            ))}
            </div>{/* end minWidth */}
          </div>
        ) : (
          <p className="text-xs text-gray-400 mt-2 text-center py-4">Select times from dropdown and click Add to get started</p>
        )}
      </div>

      {/* ── Advanced Schedule Rules (optional) ─────────────────────────── */}
      {(() => {
        const rules = formData.availabilityRules || {};
        const setRules = (updates) => {
          const next = { ...rules, ...updates };
          // Drop empty keys so untouched services keep a clean {} config
          Object.keys(next).forEach((k) => {
            if (next[k] === null || next[k] === '' || next[k] === undefined) delete next[k];
          });
          updateFormData('availabilityRules', next);
        };
        const shift = rules.shiftBoundary || null;
        const setShift = (updates) => setRules({ shiftBoundary: { ...(shift || {}), ...updates } });
        const shiftDays = (shift?.days || []).map((d) => d.toLowerCase());
        const toggleShiftDay = (day) => {
          const next = shiftDays.includes(day) ? shiftDays.filter((d) => d !== day) : [...shiftDays, day];
          setShift({ days: next });
        };

        return (
          <div className="border border-gray-200 rounded-lg p-4 space-y-4" data-testid="schedule-rules-section">
            <div>
              <Label className="text-sm sm:text-base">Advanced Schedule Rules <span className="text-xs font-normal text-gray-400">(optional)</span></Label>
              <p className="text-xs text-gray-500">Guardrails that hide duration options customers shouldn&apos;t be able to pick. Leave off if you don&apos;t need them.</p>
            </div>

            {/* Daily end cap */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-medium text-gray-900">Daily end cap</p>
                <p className="text-xs text-gray-500">Hide durations that would end after this time (e.g. all trips done by 8:00 PM).</p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  className="h-9 w-32 text-sm"
                  value={rules.latestEndTime || ''}
                  onChange={(e) => setRules({ latestEndTime: e.target.value || null })}
                  data-testid="rule-latest-end-time"
                />
                {rules.latestEndTime && (
                  <button
                    type="button"
                    onClick={() => setRules({ latestEndTime: null })}
                    className="text-xs text-gray-400 hover:text-red-500"
                    title="Clear end cap"
                    data-testid="rule-latest-end-time-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Shift change boundary */}
            <div className="pt-3 border-t border-gray-100 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-medium text-gray-900">Shift change boundary</p>
                  <p className="text-xs text-gray-500">On chosen days, block bookings that span a crew shift change — unless they&apos;re long enough (e.g. only 8h+ trips may cross 3:00 PM).</p>
                </div>
                <ServiceToggle
                  checked={!!shift}
                  onChange={(checked) => {
                    if (checked) {
                      setRules({ shiftBoundary: { time: '15:00', days: ['friday', 'saturday', 'sunday'], minDurationMinutesToCross: 480 } });
                    } else {
                      setRules({ shiftBoundary: null });
                    }
                  }}
                  data-testid="rule-shift-boundary-toggle"
                />
              </div>

              {shift && (
                <div className="space-y-3" data-testid="rule-shift-boundary-config">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-gray-600">Boundary time</Label>
                      <Input
                        type="time"
                        className="h-9 text-sm"
                        value={shift.time || '15:00'}
                        onChange={(e) => setShift({ time: e.target.value })}
                        data-testid="rule-shift-boundary-time"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Min hours to cross it</Label>
                      <Input
                        type="number"
                        min={1}
                        max={24}
                        step={0.5}
                        className="h-9 text-sm"
                        value={(shift.minDurationMinutesToCross || 480) / 60}
                        onChange={(e) => setShift({ minDurationMinutesToCross: Math.round((parseFloat(e.target.value) || 8) * 60) })}
                        data-testid="rule-shift-boundary-min-hours"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1.5 block">Applies on</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {daysOfWeek.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleShiftDay(day.value)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                            shiftDays.includes(day.value)
                              ? 'bg-blue-600 border-blue-600 text-white'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-blue-400'
                          }`}
                          data-testid={`rule-shift-day-${day.value}`}
                        >
                          {day.fullLabel || day.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const Step15Review = ({ formData }) => {
  const locale = useOperatorLocale();
  const fmt = (amt) => locale.formatMoney(amt || 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <WizardStepHeader icon={Check} tone="green" title="Review & Create" subtitle="Everything looks good?" />
    
      <div className="space-y-3">
        {/* Basic Info */}
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Service Name</p>
          <p className="font-semibold">{formData.name}</p>
        </div>
      
        {formData.tagline && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Tagline</p>
            <p className="text-sm">{formData.tagline}</p>
          </div>
        )}
      
        {formData.description && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Description</p>
            <p className="text-sm text-gray-700 line-clamp-3">{formData.description}</p>
          </div>
        )}
      
        {/* Pricing */}
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">{formData.durationModel === 'day_pass' ? 'Day Pass Pricing' : formData.durationModel === 'flexible_dates' ? 'Pricing' : 'Pricing'}</p>
          <p className="font-semibold">{fmt(formData.durationOptions[0]?.price || formData.basePrice)}</p>
          {formData.durationModel === 'day_pass' ? (
            <p className="text-xs text-gray-500 font-light mt-1">Per pass • whole-day access • capacity from linked assets • {locale.currencyCode}</p>
          ) : formData.durationModel === 'flexible_dates' ? (
            <p className="text-xs text-gray-500 font-light mt-1">Per night • {locale.currencyCode}</p>
          ) : (
            <p className="text-xs text-gray-500 font-light mt-1">{formData.durationOptions[0]?.duration || 60} minutes • {locale.currencyCode}</p>
          )}
        </div>
      
      {/* Availability */}
      {formData.durationModel === 'day_pass' ? (
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Operating Days</p>
          <p className="text-sm">{(formData.dayPassDays || []).length} day{(formData.dayPassDays || []).length !== 1 ? 's' : ''} per week • all-day access (no time slots)</p>
        </div>
      ) : formData.availableTimeSlots && formData.availableTimeSlots.length > 0 && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Available Time Slots</p>
          <p className="text-sm">{formData.availableTimeSlots.length} time slot{formData.availableTimeSlots.length !== 1 ? 's' : ''} configured</p>
        </div>
      )}
      
      {/* Dynamic Pricing */}
      {formData.dynamicPricingEnabled && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Dynamic Pricing</p>
          <p className="text-sm text-green-700 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Enabled</p>
        </div>
      )}
      
      {/* Location & Meeting Point */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-500 mb-1">Location</p>
        <p className="text-sm">{formData.country} - {formData.city}</p>
        {formData.locationDetails && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Meeting Point</p>
            <p className="text-sm text-gray-800">{formData.locationDetails}</p>
          </div>
        )}
        {!formData.locationDetails && (
          <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Meeting point not set - customers won't know where to go
          </p>
        )}
        {formData.taxEnabled && (
          <p className="text-xs text-gray-600 mt-1">
            {locale.taxLabel}: {formData.taxRate}% · {formData.taxInclusive ? 'included in price' : 'added on top'}
          </p>
        )}
      </div>
      
      {/* What's Included */}
      {formData.whatsIncluded && formData.whatsIncluded.length > 0 && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-2">What's Included</p>
          <div className="space-y-1">
            {formData.whatsIncluded.map((item, i) => (
              <p key={i} className="text-xs text-gray-700 flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600 flex-shrink-0" /> {item}</p>
            ))}
          </div>
        </div>
      )}
      
      {/* Custom Sections */}
      {formData.customSections && formData.customSections.length > 0 && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-2">Additional Sections</p>
          <p className="text-sm">{formData.customSections.length} section{formData.customSections.length !== 1 ? 's' : ''} added</p>
        </div>
      )}
      
      {/* Add-ons */}
      {formData.addOns && formData.addOns.length > 0 && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-2">Add-ons</p>
          <div className="space-y-1">
            {formData.addOns.map((addon, i) => (
              <p key={i} className="text-xs text-gray-700">
                • {addon.title} {addon.price > 0 && `(+${fmt(addon.price)})`}
              </p>
            ))}
          </div>
        </div>
      )}
      
      {/* Custom Questions */}
      {formData.customQuestions && formData.customQuestions.length > 0 && (
        <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
          <p className="text-xs text-purple-700 mb-2 font-medium">Custom Questions ({formData.customQuestions.length})</p>
          <div className="space-y-1">
            {formData.customQuestions.map((q, i) => (
              <p key={i} className="text-xs text-purple-800">
                • {q.question} {q.required && <span className="text-red-500">*</span>}
              </p>
            ))}
          </div>
        </div>
      )}
      
      {/* Assets */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-500 mb-1">Assets Linked</p>
        <p className="text-sm">{formData.assetIds.length} asset{formData.assetIds.length !== 1 ? 's' : ''}</p>
      </div>
      
      {/* Booking Settings */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-500 mb-1">Booking Type</p>
        <p className="text-sm">{formData.durationModel === 'day_pass' ? 'Day Pass (whole-day access)' : formData.bookingType === 'exclusive' ? 'Per Asset (private / exclusive)' : 'Per Ticket (individual seats)'}</p>
      </div>
      
      <div className="p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-500 mb-1">Settings</p>
        <p className="text-sm">Min notice: {formData.minimumNotice || 2} hours</p>
        <p className="text-sm">Timezone: {formData.timezone}</p>
        <p className="text-sm flex items-center gap-1.5">Status: {formData.active ? <span className="inline-flex items-center gap-1 text-green-700"><Check className="w-3.5 h-3.5" /> Live</span> : <span className="text-gray-500">Not Live</span>}</p>
      </div>
    </div>
  </div>
);
};


// AI Description Generation Modal Component
const AIDescriptionModal = ({ isOpen, onClose, onGenerate, isGenerating }) => {
  const [input, setInput] = useState('');
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[9999]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">AI Description Generator</h3>
            <p className="text-sm text-gray-600 mb-5">
              Describe your service briefly and AI will create a detailed, compelling description for you.
            </p>
            
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="E.g., 3-hour boat cruise on Lake Travis with captain, drinks, and floating mat. Great for parties and celebrations."
              className="w-full min-h-[160px] px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoFocus
            />
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onGenerate(input)}
            disabled={!input.trim() || isGenerating}
            className="flex-1 bg-purple-600 hover:bg-purple-700"
          >
            {isGenerating ? 'Generating...' : '✨ Generate'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Stripe Connect Warning Modal
const StripeConnectWarning = ({ show, onClose }) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <DollarSign className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Stripe Connect Required</h3>
            <p className="text-sm text-gray-600 mb-4">
              To activate this service and accept bookings, you need to connect your Stripe account first.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              This ensures payments go directly to your account with our 5.9% platform fee.
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={onClose} variant="outline" className="flex-1">
            Got it
          </Button>
          <Button 
            onClick={() => {
              window.location.href = '/dashboard#financials';
            }}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            Go to Financials
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateServiceWizard;
